<template>
  <div id="goalsContainer" style="
        background-color: #fff;
        border: 1px solid #e0e0e0;
        border-top: 0px;
        border-radius: 0 0 3px 3px;
      ">
    <div class="margin-subpages">
      <!-- Header Controls -->
      <div class="header-controls">
        <div class="header-row header-row-primary">
          <div class="header-left">
            <el-select
              v-model="selectedTeam"
              placeholder="Select Team"
              filterable
              class="team-select"
              :class="{ 'blur-dropdown': blurMode }"
            >
              <el-option
                :label="`All Teams (${allTeamsCount})`"
                :value="'All Teams'"
                :class="{ 'blur-option': blurMode }"
              />
              <el-option
                v-for="team in teamOptions"
                :key="team.key"
                :label="team.name"
                :value="team.key"
                :class="{ 'blur-option': blurMode }"
              />
            </el-select>

            <el-input
              v-model="searchKeyword"
              placeholder="Search"
              clearable
              class="search-input"
            />

            <el-tooltip
              content="When enabled, focus alignment stats include all sub-teams"
              placement="top"
            >
              <div class="toggle-wrapper">
                <el-switch
                  v-model="includeSubteams"
                  active-color="#52c1c7"
                  inactive-color="#dcdfe6"
                />
                <span class="toggle-label">Include Sub-teams</span>
              </div>
            </el-tooltip>

            <el-tooltip
              content="Show archived goals that have been soft-deleted"
              placement="top"
            >
              <div class="toggle-wrapper">
                <el-switch
                  v-model="showArchived"
                  active-color="#909399"
                  inactive-color="#dcdfe6"
                />
                <span class="toggle-label">
                  Show Archived
                  <span v-if="archivedGoalsCount > 0" class="archived-count-badge">{{ archivedGoalsCount }}</span>
                </span>
              </div>
            </el-tooltip>
          </div>

          <div class="header-right">
            <el-select
              v-model="dateRange"
              class="date-range-select"
              @change="handleDateRangeChange"
            >
              <el-option
                v-for="option in dateRangeOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </div>
        </div>
      </div>

      <!-- Summary Cards -->
      <div v-if="!loading" class="flex-center">
        <div class="box-card-border highlights mt-12 mb-24">
          <div class="summary-metrics">
            <!-- Goals Stats -->
            <div class="flex-column metric-block">
              <div class="fs-12 fw-700 mb-12">Goals</div>
              <div class="metric-row">
                <div class="metric-item">
                  <span class="fs-28 fw-600">{{ goalsFocusStats.totalGoals }}</span>
                  <span class="fs-13 fc-909399 ml-4">total</span>
                </div>
                <span class="metric-separator">·</span>
                <div class="metric-item">
                  <span class="fs-28 fw-600" style="color: #3AC47D;">{{ goalsFocusStats.goalsWithFocus }}</span>
                  <span class="fs-13 fc-909399 ml-4">with focus</span>
                </div>
                <span class="metric-separator">·</span>
                <div class="metric-item">
                  <span class="fs-28 fw-600" style="color: #909399;">{{ goalsFocusStats.goalsWithoutFocus }}</span>
                  <span class="fs-13 fc-909399 ml-4">no focus</span>
                </div>
              </div>
            </div>

            <!-- Goal Alignment -->
            <div class="flex-column metric-block alignment-block">
              <div class="alignment-header">
                <el-tooltip content="Percentage of focus items linked to team goals" placement="top">
                  <span class="fs-12 fw-700" style="cursor: help;">Goal Alignment</span>
                </el-tooltip>
                <span v-if="historicalSummary" class="date-range-badge">
                  Last {{ dateRange }} days
                </span>
                <span v-if="loadingHistorical" class="loading-indicator">
                  <i class="el-icon-loading"></i>
                </span>
                <span v-else-if="!hasFocusDataLoaded && !historicalSummary" class="fs-11 fc-c0c4cc ml-8">
                  <i class="el-icon-info"></i> Expand teams to load
                </span>
              </div>
              <div class="alignment-content">
                <div class="alignment-value">
                  <!-- Show N/A when no applicable items (all goal_ids are null) -->
                  <template v-if="isHistoricalAlignmentApplicable">
                    <span class="fs-32 fw-600" :style="{ color: historicalAlignmentHealthColor }">{{ historicalAlignmentRate }}</span>
                    <span class="fs-16 fw-500 fc-909399">%</span>
                    <el-tooltip 
                      v-if="dateRange === 7 && goalAlignmentWeekChange && goalAlignmentWeekChange.trend !== 'no_data'"
                      effect="light" 
                      placement="top"
                      popper-class="weekly-trend-tooltip"
                    >
                      <div slot="content" class="trend-tooltip-content">
                        <div class="trend-tooltip-header">Goal Alignment Trend</div>
                        <div class="trend-sparkline">
                          <svg :viewBox="`0 0 ${goalAlignmentTrendData.length > 1 ? (goalAlignmentTrendData.length - 1) * 30 : 30} 40`" preserveAspectRatio="none">
                            <polyline
                              :points="getSparklinePoints(goalAlignmentTrendData)"
                              fill="none"
                              :stroke="goalAlignmentWeekChange.trend === 'up' ? '#3AC47D' : goalAlignmentWeekChange.trend === 'down' ? '#F44336' : '#909399'"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <circle
                              v-if="goalAlignmentTrendData.length > 0"
                              :cx="(goalAlignmentTrendData.length - 1) * 30"
                              :cy="getSparklineY(goalAlignmentTrendData[goalAlignmentTrendData.length - 1].value, goalAlignmentTrendData)"
                              r="3"
                              :fill="goalAlignmentWeekChange.trend === 'up' ? '#3AC47D' : goalAlignmentWeekChange.trend === 'down' ? '#F44336' : '#909399'"
                            />
                          </svg>
                        </div>
                        <div class="trend-tooltip-range">
                          <span>{{ goalAlignmentTrendData.length > 0 ? Math.round(goalAlignmentTrendData[0].value) : 0 }}%</span>
                          <span class="trend-arrow">→</span>
                          <span class="trend-current">{{ goalAlignmentTrendData.length > 0 ? Math.round(goalAlignmentTrendData[goalAlignmentTrendData.length - 1].value) : 0 }}%</span>
                        </div>
                        <div class="trend-tooltip-period">Last {{ goalAlignmentTrendData.length }} weeks</div>
                      </div>
                      <span 
                        class="label-change cursor-pointer"
                        :class="{
                          'text-green': goalAlignmentWeekChange.trend === 'up',
                          'text-red': goalAlignmentWeekChange.trend === 'down',
                          'fc-6A6A6A': goalAlignmentWeekChange.trend === 'neutral'
                        }"
                      >{{ goalAlignmentWeekChange.change > 0 ? '+' : '' }}{{ goalAlignmentWeekChange.change }}%</span>
                    </el-tooltip>
                  </template>
                  <template v-else>
                    <span class="fs-32 fw-600" style="color: #909399;">N/A</span>
                  </template>
                </div>
                <div class="alignment-bar-section">
                  <div class="alignment-bar">
                    <div 
                      v-if="isHistoricalAlignmentApplicable"
                      class="alignment-bar-fill" 
                      :style="{ width: Math.max(historicalAlignmentRate, 1) + '%', background: historicalAlignmentHealthColor }"
                    ></div>
                  </div>
                  <div v-if="isHistoricalAlignmentApplicable" class="alignment-stats">
                    <span class="stat aligned" :style="{ color: historicalAlignmentHealthColor }">
                      <span class="dot" :style="{ background: historicalAlignmentHealthColor }"></span>
                      {{ formatNumber(historicalAlignedCount) }} aligned
                    </span>
                    <span class="stat unaligned">
                      <span class="dot"></span>
                      {{ formatNumber(historicalUnalignedCount) }} unaligned
                    </span>
                  </div>
                  <div v-else class="alignment-stats-na">
                    <span class="fs-12 fc-909399">No goals to map against</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Goal Focus Trends Chart -->
      <div v-if="!loading && goalFocusTrendData.datasets && goalFocusTrendData.datasets.length > 0" class="goal-trends-section">
        <div class="section-header">
          <div class="fs-15 fw-700">Goal Focus Trends</div>
          <div class="section-subtitle">
            <span class="fc-909399 fs-12">{{ goalFocusTrendData.summary && goalFocusTrendData.summary.totalWeeks >= 2 ? 'Focus items distribution across goals over time' : 'Focus distribution for ' + (goalFocusTrendData.labels && goalFocusTrendData.labels[0] || 'this week') }}</span>
            <span v-if="goalFocusTrendData.summary" class="trend-summary-badge">
              {{ goalFocusTrendData.summary.totalGoals }} goals · {{ goalFocusTrendData.summary.totalWeeks }} {{ goalFocusTrendData.summary.totalWeeks === 1 ? 'week' : 'weeks' }}
            </span>
            <el-tooltip 
              v-if="goalFocusTrendData.summary && goalFocusTrendData.summary.skippedWeeks > 0" 
              effect="light" 
              placement="top"
            >
              <div slot="content" style="max-width: 250px; padding: 8px;">
                <div class="fw-600 fs-12 mb-2">Data Gap Detected</div>
                <div class="fs-11 fc-606266">
                  {{ goalFocusTrendData.summary.skippedWeeks }} {{ goalFocusTrendData.summary.skippedWeeks === 1 ? 'week was' : 'weeks were' }} skipped due to no focus items (holidays, vacations, etc.)
                </div>
              </div>
              <span class="skipped-weeks-badge">
                <i class="el-icon-info"></i>
                {{ goalFocusTrendData.summary.skippedWeeks }} {{ goalFocusTrendData.summary.skippedWeeks === 1 ? 'week' : 'weeks' }} skipped
              </span>
            </el-tooltip>
          </div>
        </div>
        <div class="split-chart">
          <!-- Show stacked area chart for 2+ weeks -->
          <div v-if="goalFocusTrendData.summary && goalFocusTrendData.summary.totalWeeks >= 2" class="chart-container" style="height: 350px; padding: 20px;">
            <canvas ref="goalTrendChart"></canvas>
          </div>
          <!-- Show horizontal stacked bar for single week -->
          <div v-else class="single-week-bar-container">
            <canvas ref="goalTrendBarChart"></canvas>
          </div>
          <div v-if="goalFocusTrendData.summary && goalFocusTrendData.summary.hasUnmatched" class="chart-footer">
            <div class="unmatched-header" @click="showUnmatchedDetails = !showUnmatchedDetails">
              <div class="unmatched-note">
                <span class="fc-909399 fs-12">
                  {{ formatNumber(goalFocusTrendData.summary.unmatchedTotal) }} focus items are not aligned to any goal
                </span>
                <div class="unmatched-breakdown">
                  <span v-if="goalFocusTrendData.summary.unmatchedNoValueTotal > 0" class="unmatched-breakdown-item">
                    <span class="unmatched-dot unmatched-dot-no-value"></span>
                    <span class="fs-11 fc-757575">{{ formatNumber(goalFocusTrendData.summary.unmatchedNoValueTotal) }} no value</span>
                  </span>
                  <span v-if="goalFocusTrendData.summary.unmatchedValueUnknownTotal > 0" class="unmatched-breakdown-item">
                    <span class="unmatched-dot unmatched-dot-unknown"></span>
                    <span class="fs-11 fc-909399">{{ formatNumber(goalFocusTrendData.summary.unmatchedValueUnknownTotal) }} value unknown</span>
                  </span>
                  <span v-if="goalFocusTrendData.summary.unmatchedHasValueTotal > 0" class="unmatched-breakdown-item">
                    <span class="unmatched-dot unmatched-dot-has-value"></span>
                    <span class="fs-11 fc-909399">{{ formatNumber(goalFocusTrendData.summary.unmatchedHasValueTotal) }} have value</span>
                  </span>
                </div>
              </div>
              <div class="unmatched-actions">
                <el-button
                  type="primary"
                  size="mini"
                  icon="el-icon-magic-stick"
                  :loading="emergentGoalsLoading"
                  @click.stop="openEmergentGoalsModal"
                  class="discover-goals-btn"
                >
                  Discover Goals
                </el-button>
                <div class="unmatched-toggle">
                  <span class="fs-12 fc-6C30CC">{{ showUnmatchedDetails ? 'Hide items' : 'View items' }}</span>
                  <i :class="showUnmatchedDetails ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" class="fc-6C30CC"></i>
                </div>
              </div>
            </div>
            
            <!-- Expandable Unmatched Items List -->
            <transition name="unmatched-expand">
              <div v-if="showUnmatchedDetails && unmatchedFocusItems.length > 0" class="unmatched-items-container">
                <div class="unmatched-list">
                  <div
                    v-for="(item, index) in unmatchedFocusItems"
                    :key="index"
                    class="unmatched-item-row"
                    :class="{ 
                      'has-value': item.valueStatus === 'has_value',
                      'no-value': item.valueStatus === 'no_value',
                      'value-unknown': item.valueStatus === 'unknown'
                    }"
                  >
                    <div class="unmatched-item-content">
                      <div class="unmatched-item-header">
                        <span 
                          class="unmatched-item-indicator" 
                          :class="{
                            'has-value': item.valueStatus === 'has_value',
                            'no-value': item.valueStatus === 'no_value',
                            'unknown': item.valueStatus === 'unknown'
                          }"
                        ></span>
                        <span class="unmatched-item-text">{{ item.itemText }}</span>
                        <el-tag v-if="item.valueStatus === 'has_value'" size="mini" type="info" class="value-badge has-value">
                          Has Value
                        </el-tag>
                        <el-tag v-else-if="item.valueStatus === 'no_value'" size="mini" type="info" class="value-badge no-value">
                          No Value
                        </el-tag>
                      </div>
                      <span v-if="item.description" class="unmatched-item-description">{{ item.description }}</span>
                    </div>
                    <div class="unmatched-item-meta">
                      <!-- Case 1: Multiple people - show avatars and count -->
                      <template v-if="item.count > 1">
                        <span class="unmatched-people-count">
                          <i class="el-icon-user"></i>
                          {{ item.count }}
                        </span>
                        <div class="unmatched-avatars-preview" :class="{ 'blur-text': blurMode }">
                          <el-tooltip
                            v-for="(person, idx) in item.people.slice(0, 3)"
                            :key="person.email"
                            :content="blurMode ? '***' : person.fullname"
                            placement="top"
                          >
                            <div
                              class="unmatched-mini-avatar"
                              :style="{
                                zIndex: 10 - idx,
                                backgroundColor: person.house_color || '#ddd',
                                backgroundImage: person.large_pic ? `url(${person.large_pic})` : 'none'
                              }"
                            >
                              <span v-if="!person.large_pic">{{ getInitials(person.fullname) }}</span>
                            </div>
                          </el-tooltip>
                          <div v-if="item.people.length > 3" class="unmatched-mini-avatar more-count">
                            +{{ item.people.length - 3 }}
                          </div>
                        </div>
                      </template>
                      <!-- Case 2: Single person - show avatar and name -->
                      <template v-else-if="item.people.length === 1">
                        <div class="unmatched-single-person" :class="{ 'blur-text': blurMode }">
                          <div
                            class="unmatched-mini-avatar"
                            :style="{
                              backgroundColor: item.people[0].house_color || '#ddd',
                              backgroundImage: item.people[0].large_pic ? `url(${item.people[0].large_pic})` : 'none'
                            }"
                          >
                            <span v-if="!item.people[0].large_pic">{{ getInitials(item.people[0].fullname) }}</span>
                          </div>
                          <span class="unmatched-person-name-label">{{ item.people[0].fullname }}</span>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading">
        <!-- Summary Cards Skeleton -->
        <div class="flex-center">
          <div class="box-card-border highlights mt-12 mb-24">
            <div class="grid-4-20">
              <div v-for="i in 4" :key="`metric-${i}`" class="flex-column mr-24">
                <div class="skeleton-label mb-12"></div>
                <div class="skeleton-value"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Table Skeleton -->
        <div style="margin-top: 32px;">
          <div v-for="i in 5" :key="`row-${i}`" class="table-row-skeleton">
            <div class="skeleton-color-dot"></div>
            <div class="skeleton-team-name"></div>
            <div class="skeleton-goals"></div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="rootTeams.length === 0" class="center" style="padding: 80px 0;">
        <div style="text-align: center;">
          <div style="font-size: 48px; color: #dcdfe6; margin-bottom: 16px;">
            <i class="el-icon-document"></i>
          </div>
          <div style="font-size: 18px; font-weight: 600; color: #777777; margin-bottom: 8px;">
            {{ searchKeyword ? 'No Teams Found' : 'No Teams Available' }}
          </div>
          <div style="font-size: 14px; color: #909399;">
            {{ searchKeyword ? 'Try adjusting your search terms or filters' : 'Teams will appear here once they are created' }}
          </div>
        </div>
      </div>

      <!-- Tree Table -->
      <div v-else-if="!loading">
        <el-table
          :data="rootTeams"
          style="width: 100%"
          row-key="key"
          ref="goalsTable"
          lazy
          :load="loadChildren"
          :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
          :expand-row-keys="expandedRowKeys"
          class="goals-table"
        >
          <!-- Team Name Column -->
          <el-table-column min-width="240">
            <template slot="header">
              <span class="column-header-text">TEAM</span>
            </template>
            <template slot-scope="scope">
              <div class="team-cell" :class="{ 'blur-text': blurMode }">
                <div
                  class="team-color-indicator"
                  :style="{ background: scope.row.color || '#747474' }"
                ></div>
                <span class="team-name-text">{{ scope.row.name }}</span>
              </div>
            </template>
          </el-table-column>
          

          <!-- Goals Column -->
          <el-table-column min-width="550">
            <template slot="header">
              <span class="column-header-text">GOALS</span>
            </template>
            <template slot-scope="scope">
              <div v-if="teamHasGoals(scope.row.key)" class="goals-display">
                <el-collapse v-model="activeGoals" class="goals-collapse">
                  <el-collapse-item
                    v-for="goal in formatGoals(scope.row.key)"
                    :key="goal.goal_id"
                    :name="`${scope.row.key}_${goal.goal_id}`"
                    class="goal-collapse-item"
                    :class="{ 'goal-archived': goal.archived }"
                  >
                    <template slot="title">
                      <div class="goal-preview-header" :class="{ 'editing': isEditingGoal(goal.goal_id), 'archived': goal.archived }">
                        <span
                          class="goal-bullet"
                          :class="{ 'no-focus': goal.focusStats && goal.focusStats.totalItems === 0, 'archived': goal.archived }"
                          :style="!goal.archived && goal.focusStats && goal.focusStats.totalItems > 0 ? { backgroundColor: getGoalColor(goal.goal_id) } : {}"
                        ></span>
                        
                        <!-- Inline edit mode for goal name -->
                        <template v-if="isEditingGoal(goal.goal_id)">
                          <div class="goal-edit-header-row" @click.stop>
                            <el-input
                              v-model="editQuickPreview"
                              size="small"
                              class="goal-edit-input"
                              placeholder="Goal name"
                              maxlength="30"
                              @keydown.native="handleEditKeydown"
                            ></el-input>
                            <span class="inline-char-counter" :class="{ 'at-limit': editQuickPreview && editQuickPreview.length >= 30 }">{{ editQuickPreview ? editQuickPreview.length : 0 }}/30</span>
                            <div class="goal-edit-header-actions">
                              <el-tooltip content="Cancel" placement="top">
                                <button 
                                  class="goal-action-icon cancel-icon"
                                  @click.stop="cancelEditGoal"
                                  :disabled="savingGoal"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                  </svg>
                                </button>
                              </el-tooltip>
                              <el-tooltip content="Save changes" placement="top">
                                <button 
                                  class="goal-action-icon save-icon"
                                  @click.stop="confirmSaveGoal"
                                  :disabled="savingGoal"
                                >
                                  <svg v-if="!savingGoal" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  </svg>
                                  <i v-else class="el-icon-loading"></i>
                                </button>
                              </el-tooltip>
                            </div>
                          </div>
                        </template>
                        
                        <!-- Normal display mode -->
                        <template v-else>
                          <span class="goal-quick-text" :class="{ 'archived-text': goal.archived }">{{ goal.quick_preview }}</span>
                          
                          <!-- Archived badge -->
                          <el-tag
                            v-if="goal.archived"
                            size="mini"
                            type="info"
                            class="archived-badge"
                          >
                            Archived
                          </el-tag>
                          
                          <!-- Actions for active (non-archived) goals -->
                          <template v-if="!goal.archived">
                            <el-tooltip content="Edit goal" placement="top">
                              <i 
                                class="el-icon-edit goal-edit-icon" 
                                @click="startEditGoal(goal, scope.row.key, $event)"
                              ></i>
                            </el-tooltip>
                            <!-- Link to parent goals -->
                            <el-tooltip 
                              :content="getParentGoalLinkTooltip(goal)"
                              placement="top"
                              :open-delay="200"
                            >
                              <i 
                                class="el-icon-link goal-link-icon"
                                :class="{ 'has-links': goal.parent_goal_ids && goal.parent_goal_ids.length > 0 }"
                                @click="openLinkingModal(goal, scope.row.key, $event)"
                              ></i>
                            </el-tooltip>
                            <el-tooltip content="Archive goal" placement="top">
                              <i 
                                class="el-icon-folder goal-archive-icon" 
                                @click="confirmArchiveGoal(goal, scope.row.key, $event)"
                              ></i>
                            </el-tooltip>
                            <el-tooltip content="Delete goal permanently" placement="top">
                              <i 
                                class="el-icon-delete goal-delete-icon" 
                                @click="confirmDeleteGoal(goal, scope.row.key, $event)"
                              ></i>
                            </el-tooltip>
                          </template>
                          
                          <!-- Actions for archived goals -->
                          <template v-else>
                            <el-tooltip content="Restore goal" placement="top">
                              <i 
                                class="el-icon-refresh-left goal-unarchive-icon" 
                                @click="confirmUnarchiveGoal(goal, scope.row.key, $event)"
                              ></i>
                            </el-tooltip>
                            <el-tooltip content="Delete goal permanently" placement="top">
                              <i 
                                class="el-icon-delete goal-delete-icon" 
                                @click="confirmDeleteGoal(goal, scope.row.key, $event)"
                              ></i>
                            </el-tooltip>
                          </template>
                        </template>
                        
                        <!-- <el-tag
                          v-if="goal.goal_id && !goal.goal_id.startsWith('legacy') && !isEditingGoal(goal.goal_id)"
                          size="mini"
                          type="info"
                          class="ai-badge"
                        >
                          ✨ AI
                        </el-tag> -->
                        <!-- Focus stats when goal has focus -->
                        <el-tooltip
                          v-if="goal.focusStats && goal.focusStats.totalItems > 0"
                          :content="`${goal.focusStats.peopleCount} people focusing on this goal`"
                          placement="top"
                        >
                          <span class="focus-stats-badge">
                            <i class="el-icon-user"></i>
                            <span class="people-num">{{ goal.focusStats.peopleCount }}</span>
                            <span class="stats-separator">|</span>
                            <span class="items-count">
                              <span class="open-num">{{ goal.focusStats.openCount }}</span>
                              <span class="items-slash">/</span>
                              <span class="done-num">{{ goal.focusStats.doneCount }}</span>
                            </span>
                          </span>
                        </el-tooltip>
                        <!-- Loading state -->
                        <span
                          v-else-if="isLoadingFocus(scope.row.key)"
                          class="loading-focus-badge"
                        >
                          <i class="el-icon-loading"></i>
                        </span>


                        <!-- No focus warning - more prominent -->
                        <el-tooltip
                          v-else-if="goal.focusStats && goal.focusStats.totalItems === 0"
                          content="No one is focusing on this goal"
                          placement="top"
                        >
                          <span class="no-focus-badge">
                            <i class="el-icon-warning-outline"></i> No focus
                          </span>
                        </el-tooltip>
                        <!-- Parent goal links indicator -->
                        <el-tooltip
                          v-if="goal.parent_goal_ids && goal.parent_goal_ids.length > 0 && !isEditingGoal(goal.goal_id)"
                          placement="top"
                          effect="light"
                          popper-class="parent-goals-tooltip"
                        >
                          <div slot="content" class="parent-goals-tooltip-content">
                            <div class="tooltip-header">Linked to parent goals:</div>
                            <div 
                              v-for="pg in getParentGoalDetailsComputed(goal.parent_goal_ids)" 
                              :key="pg.goal_id"
                              class="tooltip-goal-item"
                            >
                              <span class="tooltip-team-dot" :style="{ backgroundColor: pg.teamColor }"></span>
                              <span class="tooltip-team-name">{{ pg.teamName }}</span>
                              <span class="tooltip-arrow">→</span>
                              <span class="tooltip-goal-name">{{ pg.quick_preview }}</span>
                            </div>
                          </div>
                          <span class="parent-links-badge" @click="openLinkingModal(goal, scope.row.key, $event)">
                            <i class="el-icon-link"></i>
                            {{ goal.parent_goal_ids.length }}
                          </span>
                        </el-tooltip>
                        <!-- People avatars preview -->
                        <div v-if="goal.focusStats && goal.focusStats.people && goal.focusStats.people.length > 0" class="people-avatars-preview" :class="{ 'blur-text': blurMode }">
                          <el-tooltip
                            v-for="(person, idx) in goal.focusStats.people.slice(0, 3)"
                            :key="person.email"
                            :content="blurMode ? '***' : person.fullname"
                            placement="top"
                          >
                            <div
                              class="mini-avatar"
                              :style="{
                                zIndex: 10 - idx,
                                backgroundColor: person.house_color || '#ddd',
                                backgroundImage: person.large_pic ? `url(${person.large_pic})` : 'none'
                              }"
                            >
                              <span v-if="!person.large_pic">{{ getInitials(person.fullname) }}</span>
                            </div>
                          </el-tooltip>
                          <div v-if="goal.focusStats.people.length > 3" class="mini-avatar more-count">
                            +{{ goal.focusStats.people.length - 3 }}
                          </div>
                        </div>
                      </div>
                    </template>
                    <div class="goal-full-description">
                      <!-- Inline edit mode for description -->
                      <template v-if="isEditingGoal(goal.goal_id)">
                        <el-input
                          type="textarea"
                          v-model="editDescription"
                          :autosize="{ minRows: 2, maxRows: 6 }"
                          class="goal-edit-textarea"
                          placeholder="Goal description"
                          @keydown.native="handleEditKeydown"
                        ></el-input>
                        <p class="goal-edit-hint">Edit the goal description above. Use the Save button in the header to confirm changes.</p>
                      </template>
                      
                      <!-- Normal display mode -->
                      <template v-else>
                        {{ goal.goal_description }}
                      </template>
                      
                      <!-- Parent goal links section in expanded view -->
                      <div v-if="!isEditingGoal(goal.goal_id) && !goal.archived" class="goal-parent-links-section">
                        <div class="parent-links-header">
                          <span class="parent-links-title">
                            <i class="el-icon-link"></i>
                            Parent Goal Links
                          </span>
                          <el-button 
                            type="text" 
                            size="mini"
                            class="edit-links-btn"
                            @click="openLinkingModal(goal, scope.row.key, $event)"
                          >
                            {{ goal.parent_goal_ids && goal.parent_goal_ids.length > 0 ? 'Edit Links' : 'Add Links' }}
                          </el-button>
                        </div>
                        <div v-if="goal.parent_goal_ids && goal.parent_goal_ids.length > 0" class="parent-links-list">
                          <div 
                            v-for="pg in getParentGoalDetailsComputed(goal.parent_goal_ids)" 
                            :key="pg.goal_id"
                            class="parent-link-item"
                          >
                            <span class="parent-link-dot" :style="{ backgroundColor: pg.teamColor }"></span>
                            <span class="parent-link-team">{{ pg.teamName }}</span>
                            <span class="parent-link-arrow">→</span>
                            <span class="parent-link-goal">{{ pg.quick_preview }}</span>
                          </div>
                        </div>
                        <div v-else class="no-parent-links">
                          <span class="no-links-text">No parent goals linked yet</span>
                        </div>
                      </div>
                      
                      <!-- Show all people focusing on this goal when expanded -->
                      <div v-if="!isEditingGoal(goal.goal_id) && goal.focusStats && goal.focusStats.people && goal.focusStats.people.length > 0" class="goal-people-list">
                        <div class="goal-people-header">People focusing on this goal:</div>
                        <div class="goal-people-grid" :class="{ 'blur-text': blurMode }">
                          <div v-for="person in goal.focusStats.people" :key="person.email" class="person-chip">
                            <div
                              class="person-chip-avatar"
                              :style="{
                                backgroundColor: person.house_color || '#ddd',
                                backgroundImage: person.large_pic ? `url(${person.large_pic})` : 'none'
                              }"
                            >
                              <span v-if="!person.large_pic">{{ getInitials(person.fullname) }}</span>
                            </div>
                            <span class="person-chip-name">{{ person.fullname }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </el-collapse-item>
                </el-collapse>
              </div>
              <div v-else class="no-goals-text">
                {{ $t("goals.no_goals") }}
              </div>
            </template>
          </el-table-column>

          <!-- Actions Column -->
          <el-table-column width="120" align="right">
            <template slot="header">
              <span class="column-header-text"></span>
            </template>
            <template slot-scope="scope">
              <el-button
                v-if="teamHasGoals(scope.row.key)"
                type="text"
                @click="editGoals(scope.row)"
                class="action-btn edit-action"
              >
                {{ $t("goals.edit_goals") }}
              </el-button>
              <el-button
                v-else
                type="text"
                @click="addGoals(scope.row)"
                class="action-btn add-action"
              >
                <i class="el-icon-plus"></i>
                {{ $t("goals.add_goals") }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- Goals Edit/Add Modal -->
    <div id="goalsModal">
      <el-dialog
        top="10vh"
        :visible.sync="showModal"
        width="700px"
        :close-on-click-modal="false"
        :append-to-body="false"
      >
        <div class="px-32 py-24 modal-header" :class="[ blurMode ? 'blurelement': '' ]">
          {{ modalTitle }}
        </div>
        <div class="px-32 pb-24">
          <el-form v-if="editingTeam" label-position="top">
            <el-form-item class="mt-20">
              <span slot="label" class="fs-15 fw-700">{{ $t('goals.team_name') }}</span>
              <div class="team-name-display" :class="{ 'blur-text': blurMode }">
                <div
                  class="team-color-tag"
                  :style="{ background: editingTeam.color || '#747474' }"
                ></div>
                <span class="fs-16">{{ editingTeam.name }}</span>
              </div>
            </el-form-item>

            <!-- Tab Navigation for Freeform vs Advanced -->
            <el-tabs v-model="goalInputMode" class="goals-input-tabs mt-24">
              <!-- Freeform Tab (AI-assisted) -->
              <el-tab-pane label="Freeform" name="freeform">
                <div class="tab-description">
                  <i class="el-icon-magic-stick"></i>
                  Enter your goals as free text. AI will structure them into individual goals with descriptions.
                </div>
                <el-input
                  type="textarea"
                  v-model="goalsText"
                  :autosize="{ minRows: 5, maxRows: 10 }"
                  :placeholder="$t('goals.goals_placeholder')"
                  class="goals-textarea"
                ></el-input>
                <p class="goals-hint">{{ $t("goals.goals_hint") }}</p>
              </el-tab-pane>

              <!-- Advanced Tab (Structured input) -->
              <el-tab-pane label="Advanced" name="advanced">
                <div class="tab-description">
                  <i class="el-icon-edit-outline"></i>
                  {{ advancedGoals.some(g => g.goal_id) ? 'Edit existing goals or add new ones.' : 'Add goals directly with titles and descriptions. No AI transformation required.' }}
                </div>
                
                <div class="advanced-goals-list">
                  <div 
                    v-for="(goal, index) in advancedGoals" 
                    :key="goal.goal_id || `new-${index}`" 
                    class="advanced-goal-item"
                    :class="{ 'is-existing': goal.goal_id, 'is-new': !goal.goal_id }"
                  >
                    <div class="goal-item-header">
                      <div class="goal-item-header-left">
                        <span class="goal-number">Goal {{ index + 1 }}</span>
                        <span v-if="goal.goal_id" class="goal-status-badge existing">
                          <i class="el-icon-check"></i> Existing
                        </span>
                        <span v-else class="goal-status-badge new">
                          <i class="el-icon-plus"></i> New
                        </span>
                      </div>
                      <el-button
                        v-if="!goal.goal_id && advancedGoals.length > 1"
                        type="text"
                        icon="el-icon-delete"
                        class="remove-goal-btn"
                        @click="removeAdvancedGoal(index)"
                      ></el-button>
                    </div>
                    <el-input
                      v-model="goal.title"
                      placeholder="Goal title (e.g., Increase customer satisfaction)"
                      class="goal-title-input"
                      maxlength="30"
                    ></el-input>
                    <div class="char-counter" :class="{ 'at-limit': goal.title && goal.title.length >= 30 }">
                      {{ goal.title ? goal.title.length : 0 }}/30
                    </div>
                    <el-input
                      type="textarea"
                      v-model="goal.description"
                      :autosize="{ minRows: 2, maxRows: 4 }"
                      placeholder="Description (optional - provide more details about this goal)"
                      class="goal-description-input"
                    ></el-input>
                    
                    <!-- Parent Goal Linking Section -->
                    <div class="goal-parent-linking" v-if="ancestorGoalsForEditingTeam.length > 0">
                      <div class="parent-linking-label">
                        <i class="el-icon-link"></i>
                        <span>Link to Parent Goals</span>
                        <span v-if="goal.parent_goal_ids && goal.parent_goal_ids.length > 0" class="linked-count">
                          {{ goal.parent_goal_ids.length }} linked
                        </span>
                      </div>
                      <el-select
                        v-model="goal.parent_goal_ids"
                        multiple
                        filterable
                        collapse-tags
                        placeholder="Select parent goals to align with..."
                        class="parent-goals-select"
                      >
                        <el-option-group
                          v-for="group in ancestorGoalsForEditingTeam"
                          :key="group.team.key"
                          :label="group.team.name"
                        >
                          <el-option
                            v-for="pg in group.goals"
                            :key="pg.goal_id"
                            :label="pg.quick_preview"
                            :value="pg.goal_id"
                          >
                            <div class="parent-goal-option">
                              <span class="pg-team-dot" :style="{ backgroundColor: group.team.color }"></span>
                              <span class="pg-goal-text">{{ pg.quick_preview }}</span>
                            </div>
                          </el-option>
                        </el-option-group>
                      </el-select>
                    </div>
                    <div class="goal-parent-linking no-parents" v-else-if="editingTeam">
                      <div class="no-parents-hint">
                        <i class="el-icon-info"></i>
                        <span>No parent team goals available to link</span>
                      </div>
                    </div>
                  </div>
                </div>

                <el-button 
                  type="text" 
                  icon="el-icon-plus"
                  class="add-goal-btn"
                  @click="addAdvancedGoal"
                >
                  Add another goal
                </el-button>
              </el-tab-pane>
            </el-tabs>
          </el-form>

          <div class="dialog-footer">
            <el-button class="cancel-btn" @click="cancelEdit">{{ $t("goals.cancel") }}</el-button>
            <el-button class="save-btn" @click="saveGoals" :loading="saving">
              {{ $t("goals.save") }}
            </el-button>
          </div>
        </div>
      </el-dialog>
    </div>
    
    <!-- Parent Goal Linking Modal -->
    <el-dialog
      id="goalLinkingModal"
      top="10vh"
      :visible.sync="linkingModalVisible"
      width="520px"
      :close-on-click-modal="false"
      :append-to-body="true"
      :show-close="false"
      custom-class="goal-linking-dialog"
      @close="closeLinkingModal"
    >
      <!-- Custom Header -->
      <div slot="title" class="linking-dialog-header">
        <div class="linking-header-content">
          <div class="linking-header-icon">
            <i class="el-icon-connection"></i>
          </div>
          <div class="linking-header-text">
            <div class="linking-header-title">Link to Parent Goals</div>
            <div class="linking-header-subtitle">Align this goal with higher-level objectives</div>
          </div>
        </div>
        <button class="linking-close-btn" @click="closeLinkingModal">
          <i class="el-icon-close"></i>
        </button>
      </div>
      
      <div class="linking-dialog-body">
        <!-- Current Goal Card -->
        <div v-if="linkingGoal" class="current-goal-card">
          <div class="current-goal-label">
            <i class="el-icon-aim"></i>
            Current Goal
          </div>
          <div class="current-goal-name">{{ linkingGoal.quick_preview }}</div>
        </div>
        
        <!-- Parent Goals Selection -->
        <div class="parent-goals-section">
          <!-- Empty State -->
          <div v-if="ancestorGoalsGrouped.length === 0" class="empty-parents-state">
            <div class="empty-state-icon">
              <i class="el-icon-folder-opened"></i>
            </div>
            <div class="empty-state-title">No Parent Goals Available</div>
            <div class="empty-state-desc">
              Parent teams need to have goals set up before you can create alignment links.
            </div>
          </div>
          
          <!-- Goals List -->
          <div v-else class="parent-goals-container">
            <div class="parent-goals-header">
              <span class="parent-goals-label">Select Parent Goals</span>
              <transition name="fade">
                <span v-if="selectedParentGoalIds.length > 0" class="selection-badge">
                  <i class="el-icon-check"></i>
                  {{ selectedParentGoalIds.length }} linked
                </span>
              </transition>
            </div>
            
            <div class="parent-goals-scroll">
              <div 
                v-for="teamGroup in ancestorGoalsGrouped" 
                :key="teamGroup.team.key"
                class="team-goal-group"
              >
                <div class="team-goal-header">
                  <span class="team-indicator" :style="{ backgroundColor: teamGroup.team.color }"></span>
                  <span class="team-name">{{ teamGroup.team.name }}</span>
                  <span class="team-count">{{ teamGroup.goals.length }}</span>
                </div>
                <div class="team-goal-items">
                  <div
                    v-for="goal in teamGroup.goals"
                    :key="goal.goal_id"
                    class="goal-select-item"
                    :class="{ 'is-checked': selectedParentGoalIds.includes(goal.goal_id) }"
                    @click="toggleParentGoal(goal.goal_id)"
                  >
                    <div class="goal-select-checkbox">
                      <transition name="check-scale">
                        <i v-if="selectedParentGoalIds.includes(goal.goal_id)" class="el-icon-check"></i>
                      </transition>
                    </div>
                    <span class="goal-select-text">{{ goal.quick_preview }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Footer -->
      <div slot="footer" class="linking-dialog-footer">
        <el-button class="linking-cancel-btn" @click="closeLinkingModal">
          Cancel
        </el-button>
        <el-button 
          class="linking-save-btn"
          :class="{ 'has-selection': selectedParentGoalIds.length > 0 }"
          @click="saveGoalLinks" 
          :loading="savingLinks"
          :disabled="ancestorGoalsGrouped.length === 0"
        >
          <template v-if="!savingLinks">
            <i :class="selectedParentGoalIds.length > 0 ? 'el-icon-check' : 'el-icon-delete'"></i>
            {{ selectedParentGoalIds.length > 0 ? `Save ${selectedParentGoalIds.length} Link${selectedParentGoalIds.length > 1 ? 's' : ''}` : 'Remove All Links' }}
          </template>
        </el-button>
      </div>
    </el-dialog>

    <!-- Emergent Goals Modal -->
    <EmergentGoalsModal
      :visible.sync="emergentGoalsModalVisible"
      :loading="emergentGoalsLoading"
      :result="emergentGoalsResult"
      :teamId="selectedTeam"
      :teamName="selectedTeamName"
      :unmatchedFocusItems="emergentGoalsUnmatchedItems"
      :totalFocusItems="emergentGoalsTotalFocusItems"
      @close="closeEmergentGoalsModal"
      @retry="discoverEmergentGoals"
      @assignment-accepted="onAssignmentAccepted"
      @assignments-auto-applied="onAssignmentsAutoApplied"
      @goal-created="onGoalCreated"
    />
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import _ from "lodash";
import { DATE_RANGE_OPTIONS } from "@/store/focus";
import EmergentGoalsModal from "./EmergentGoalsModal.vue";

export default {
  name: "goals",
  components: {
    EmergentGoalsModal,
  },
  data() {
    return {
      goalsText: "",
      saving: false,
      loading: false,
      activeGoals: [], // Track which goals are expanded in collapse
      expandedRowKeys: [], // Controls which tree rows are expanded
      initialExpandDone: false, // Ensure default expand happens only once
      expandRetryCount: 0, // Limit retry attempts
      includeSubteams: true, // Toggle for including sub-team focus data
      teamFocusStats: {}, // Local cache for team alignment stats
      dateRangeOptions: DATE_RANGE_OPTIONS, // Date range options for historical data
      // Inline goal editing state
      editingGoalId: null, // ID of the goal currently being edited
      editingTeamIdForGoal: null, // Team ID of the goal being edited
      editQuickPreview: '', // Temporary value for quick_preview during edit
      editDescription: '', // Temporary value for goal_description during edit
      originalQuickPreview: '', // Original value to revert on cancel
      originalDescription: '', // Original value to revert on cancel
      savingGoal: false, // Loading state for saving individual goal
      showArchived: false, // Toggle for showing archived goals
      archivingGoal: false, // Loading state for archiving/unarchiving
      deletingGoal: false, // Loading state for deleting
      // Goal trends chart
      goalTrendChartInstance: null, // Chart.js instance for goal trends (area chart)
      goalTrendBarChartInstance: null, // Chart.js instance for single-week bar chart
      // Modal input mode (freeform vs advanced)
      goalInputMode: 'freeform', // 'freeform' | 'advanced'
      advancedGoals: [{ title: '', description: '', parent_goal_ids: [] }], // Dynamic list for advanced mode
      // Unmatched focus items display
      showUnmatchedDetails: false, // Controls expand/collapse of unmatched items list
      activeUnmatchedItems: [], // Track which unmatched items are expanded in collapse
      // Parent goal linking modal state
      linkingModalVisible: false, // Controls visibility of linking modal
      linkingGoal: null, // The goal being linked
      linkingTeamId: null, // Team ID of the goal being linked
      selectedParentGoalIds: [], // Selected parent goal IDs in the modal
      savingLinks: false, // Loading state for saving links
      // Emergent Goals modal state
      emergentGoalsModalVisible: false, // Controls visibility of emergent goals modal
      emergentGoalsUnmatchedItems: [], // Unmatched focus items for team attribution
      emergentGoalsTotalFocusItems: 0, // Total focus items for percentage calculation
      emergentGoalsDiscoveryUsesUnassigned: false, // True when using goal_id === null items (no-goals teams)
      emergentGoalsDiscoveryNoGoalsInScope: false, // True when selected scope has zero in-scope goals
      // Debounced reload function (initialized in created)
      debouncedReloadData: null,
    };
  },
  computed: {
    user() {
      return this.$store.getters["authen/user"];
    },
    teams() {
      return this.$store.getters["goals/teams"];
    },
    filteredTeams() {
      return this.$store.getters["goals/filteredTeams"];
    },
    rootTeams() {
      const allTeams = this.filteredTeams;
      const selectedTeamId = this.selectedTeam;
      
      // When searching, show all matching teams (not just root-level)
      if (this.searchKeyword && this.searchKeyword.trim().length > 0) {
        return allTeams.map(team => ({
          ...team,
          hasChildren: false // Don't show expand for search results
        }));
      }
      
      // Build a Set of parent IDs for O(1) lookup instead of O(n) per team
      const parentIds = new Set();
      for (const team of allTeams) {
        if (team.parent) {
          parentIds.add(team.parent);
        }
      }
      
      // When a specific team is selected, show that team as the root
      // Otherwise, show actual root-level teams (no parent or parent='root')
      const result = [];
      for (const team of allTeams) {
        const isSelectedTeam = selectedTeamId && selectedTeamId !== 'All Teams' && team.key === selectedTeamId;
        const isRootLevel = !team.parent || team.parent === 'root';
        const isDirectChildOfSelected = selectedTeamId && selectedTeamId !== 'All Teams' && team.parent === selectedTeamId;
        
        // Show team if:
        // 1. It's the selected team (treat as root)
        // 2. It's a root-level team (when "All Teams" selected)
        // 3. It's a direct child of the selected team
        if (isSelectedTeam || (selectedTeamId === 'All Teams' && isRootLevel) || (!selectedTeamId && isRootLevel)) {
          result.push({
            ...team,
            hasChildren: parentIds.has(team.key), // O(1) lookup
          });
        }
      }
      return result;
    },
    selectedTeam: {
      get() {
        return this.$store.getters["goals/selectedTeam"];
      },
      set(value) {
        this.$store.commit("goals/setSelectedTeam", value);
      },
    },
    searchKeyword: {
      get() {
        return this.$store.getters["goals/searchKeyword"];
      },
      set(value) {
        this.$store.commit("goals/setSearchKeyword", value);
      },
    },
    teamOptions() {
      return this.$store.getters["team/teamsall"];
    },
    allTeamsCount() {
      return this.$store.getters["team/teamsall"].length;
    },
    editingTeamId() {
      return this.$store.getters["goals/editingTeamId"];
    },
    editingTeam() {
      return this.$store.getters["goals/editingTeam"];
    },
    showModal: {
      get() {
        return this.editingTeamId !== null;
      },
      set(value) {
        if (!value) {
          this.$store.dispatch("goals/closeEditModal");
        }
      }
    },
    modalTitle() {
      if (!this.editingTeam) return "";
      return this.editingTeam.goals
        ? this.$t("goals.edit_goals_title")
        : this.$t("goals.add_goals_title");
    },
    summaryStats() {
      const teamsWithGoals = this.filteredTeams.filter(t => t.goals && t.goals.trim().length > 0).length;
      const teamsWithoutGoals = this.filteredTeams.length - teamsWithGoals;
      const totalGoalsCount = this.filteredTeams.reduce((sum, team) => {
        if (!team.goals) return sum;
        const goals = team.goals.split('\n').map(g => g.trim()).filter(g => g.length > 0);
        return sum + goals.length;
      }, 0);

      return {
        totalTeams: this.filteredTeams.length,
        teamsWithGoals,
        teamsWithoutGoals,
        totalGoalsCount,
      };
    },
    blurMode() {
      return this.$store.getters["demomode/blurMode"];
    },
    goalsTeamAI() {
      return this.$store.getters["goals/goalsTeamAI"];
    },
    goalsWithFocusStats() {
      return this.$store.getters["goals/goalsWithFocusStats"];
    },
    archivedGoalsCount() {
      return this.$store.getters["goals/archivedGoalsCount"];
    },
    activeGoalsTeamAI() {
      return this.$store.getters["goals/activeGoalsTeamAI"];
    },
    archivedGoalsTeamAI() {
      return this.$store.getters["goals/archivedGoalsTeamAI"];
    },
    // Pre-build children index for O(1) lookups in BFS traversal
    childrenByParent() {
      const index = {};
      this.filteredTeams.forEach((team) => {
        if (team.parent) {
          if (!index[team.parent]) {
            index[team.parent] = [];
          }
          index[team.parent].push(team.key);
        }
      });
      return index;
    },
    // Get all descendant team IDs for a given team (optimized with childrenByParent index)
    selectedTeamDescendants() {
      const selectedTeamId = this.selectedTeam;
      if (!selectedTeamId || selectedTeamId === 'All Teams') {
        // Return all team IDs when "All Teams" is selected
        return new Set(this.filteredTeams.map(t => t.key));
      }

      const descendants = new Set([selectedTeamId]);

      if (this.includeSubteams) {
        // BFS using pre-built children index for O(1) child lookups
        const queue = [selectedTeamId];
        const childrenIndex = this.childrenByParent;

        while (queue.length > 0) {
          const parentId = queue.shift();
          const children = childrenIndex[parentId] || [];
          for (const childKey of children) {
            if (!descendants.has(childKey)) {
              descendants.add(childKey);
              queue.push(childKey);
            }
          }
        }
      }

      return descendants;
    },
    // Aggregate all unique focus items from loaded teams (deduped to avoid double-counting)
    // Scoped to the selected team and its descendants
    // Three-state alignment: null = ignored, "unmatched" = unaligned, valid goal_id = aligned
    aggregatedFocusItems() {
      const seenItemIds = new Set();
      const items = { aligned: 0, unaligned: 0 };
      
      // Get the appropriate suffix based on toggle state
      const suffix = this.includeSubteams ? '_with_children' : '_direct';
      
      // Get the set of team IDs to include based on selection
      const relevantTeamIds = this.selectedTeamDescendants;
      
      // Iterate through all loaded team focus data
      Object.keys(this.focusDataByTeam || {}).forEach((cacheKey) => {
        // Only count from the correct includeSubteams variant
        if (!cacheKey.endsWith(suffix)) return;
        
        // Extract team ID from cache key (format: "teamId_with_children" or "teamId_direct")
        const teamId = cacheKey.replace(suffix, '').replace(/_$/, '');
        
        // Only count if this team is in our relevant set
        if (!relevantTeamIds.has(teamId)) return;
        
        const cached = this.focusDataByTeam[cacheKey];
        if (cached && cached.focusItems) {
          cached.focusItems.forEach((item) => {
            // Use item_id + userEmail to uniquely identify each focus item
            const itemKey = `${item.item_id}_${item.userEmail}`;
            if (!seenItemIds.has(itemKey)) {
              seenItemIds.add(itemKey);
              // Three-state: null = ignored, "unmatched" = unaligned, valid goal_id = aligned
              if (item.goal_id && item.goal_id !== 'unmatched') {
                items.aligned++;
              } else if (item.goal_id === 'unmatched') {
                items.unaligned++;
              }
              // null goal_id items are ignored (not counted)
            }
          });
        }
      });
      
      return items;
    },
    // Calculate aligned/unaligned counts from aggregated focus data
    alignedFocusCount() {
      return this.aggregatedFocusItems.aligned;
    },
    unalignedFocusCount() {
      return this.aggregatedFocusItems.unaligned;
    },
    // Alignment rate as a percentage (null when no applicable items = N/A)
    alignmentRate() {
      const total = this.alignedFocusCount + this.unalignedFocusCount;
      if (total === 0) return null; // N/A - no applicable items
      return Math.round((this.alignedFocusCount / total) * 100);
    },
    // Check if alignment data is applicable (not N/A)
    isAlignmentApplicable() {
      return this.alignmentRate !== null;
    },
    // Health-based color for alignment (red < 33%, yellow 33-66%, green > 66%, gray for N/A)
    alignmentHealthColor() {
      const rate = this.alignmentRate;
      if (rate === null) return '#909399'; // Gray for N/A
      if (rate < 33) return '#F44336'; // Red
      if (rate < 66) return '#FF9800'; // Orange/Yellow
      return '#3AC47D'; // Green
    },
    // Pre-compute team focus stats map for all relevant teams (avoids repeated method calls in loops)
    teamFocusStatsMap() {
      const suffix = this.includeSubteams ? 'with_children' : 'direct';
      const relevantTeamIds = this.selectedTeamDescendants;
      const statsMap = {};

      for (const teamId of relevantTeamIds) {
        const cacheKey = `${teamId}_${suffix}`;
        statsMap[teamId] = this.teamFocusStats[cacheKey] || null;
      }

      return statsMap;
    },
    // Goals focus statistics (total goals, with focus, without focus)
    // Uses the same data source as formatGoals() for consistency
    goalsFocusStats() {
      const relevantTeamIds = this.selectedTeamDescendants;
      const goalsTeamAI = this.goalsTeamAI || {};
      const focusStatsMap = this.teamFocusStatsMap;

      let totalGoals = 0;
      let goalsWithFocus = 0;

      // Iterate through teams in our scope
      for (const teamId of relevantTeamIds) {
        const teamGoals = goalsTeamAI[teamId];
        if (teamGoals && Array.isArray(teamGoals)) {
          // Filter out archived goals (consistent with formatGoals when showArchived is false)
          const activeGoals = teamGoals.filter(goal => !goal.archived);
          // Get pre-computed focus stats for this team
          const focusStats = focusStatsMap[teamId];

          for (const goal of activeGoals) {
            totalGoals++;

            if (focusStats && focusStats.goalStats) {
              const goalFocusStats = focusStats.goalStats[goal.goal_id];
              if (goalFocusStats && goalFocusStats.peopleCount > 0) {
                goalsWithFocus++;
              }
            }
          }
        }
      }

      return {
        totalGoals,
        goalsWithFocus,
        goalsWithoutFocus: totalGoals - goalsWithFocus
      };
    },
    // Check if focus data has been loaded for the selected team scope
    hasFocusDataLoaded() {
      const suffix = this.includeSubteams ? '_with_children' : '_direct';
      const relevantTeamIds = this.selectedTeamDescendants;
      
      // Check if we have data for at least one relevant team
      for (const cacheKey of Object.keys(this.focusDataByTeam || {})) {
        if (!cacheKey.endsWith(suffix)) continue;
        const teamId = cacheKey.replace(suffix, '').replace(/_$/, '');
        if (relevantTeamIds.has(teamId)) {
          return true;
        }
      }
      return false;
    },
    goalAlignmentStats() {
      return this.$store.getters["goals/goalAlignmentStats"];
    },
    // Focus store getters for lazy loading
    focusDataByTeam() {
    
      return this.$store.getters["focus/focusDataByTeam"];
    },
    loadingTeamFocus() {
      return this.$store.getters["focus/loadingTeamFocus"];
    },
    // Historical focus data computed properties
    dateRange: {
      get() {
        return this.$store.getters["focus/dateRange"];
      },
      set(value) {
        this.$store.commit("focus/setDateRange", value);
      },
    },
    loadingHistorical() {
      return this.$store.getters["focus/loadingHistorical"] || 
             this.$store.getters["focus/isLoadingHistoricalTeam"](this.selectedTeam, this.includeSubteams);
    },
    historicalSummary() {
      return this.$store.getters["focus/getHistoricalSummaryStats"](this.selectedTeam, this.includeSubteams);
    },
    historicalFocusCache() {
      return this.$store.getters["focus/getHistoricalFocusForTeam"](this.selectedTeam, this.includeSubteams);
    },
    historicalGoalStats() {
      return this.$store.getters["focus/getHistoricalGoalStats"](this.selectedTeam, this.includeSubteams);
    },
    // Historical alignment counts (use historical data if available, otherwise fall back to current)
    historicalAlignedCount() {

      if (this.historicalSummary) {
        return this.historicalSummary.alignedItems;
      }
      return this.alignedFocusCount;
    },
    historicalUnalignedCount() {
      if (this.historicalSummary) {
        return this.historicalSummary.unalignedItems;
      }
      return this.unalignedFocusCount;
    },
    historicalAlignmentRate() {
      if (this.historicalSummary) {
        return this.historicalSummary.alignmentRate;
      }
      return this.alignmentRate;
    },
    // Check if historical alignment data is applicable (not N/A)
    isHistoricalAlignmentApplicable() {
      return this.historicalAlignmentRate !== null;
    },
    historicalAlignmentHealthColor() {
      const rate = this.historicalAlignmentRate;
      if (rate === null) return '#909399'; // Gray for N/A
      if (rate < 33) return '#F44336'; // Red
      if (rate < 66) return '#FF9800'; // Orange/Yellow
      return '#3AC47D'; // Green
    },
    // Weekly change computed property
    goalAlignmentWeekChange() {
      const teamId = this.selectedTeam || 'All Teams';
      return this.$store.getters["focus/getGoalAlignmentWeekChange"](teamId);
    },
    loadingWeeklySummary() {
      const teamId = this.selectedTeam || 'All Teams';
      return this.$store.getters["focus/isLoadingWeeklySummary"](teamId);
    },
    // Multi-week trend data for tooltip (last 4 weeks)
    goalAlignmentTrendData() {
      const teamId = this.selectedTeam || 'All Teams';
      return this.$store.getters["focus/getWeeklyTrendData"](teamId, 'alignedItemsPercentage', 4);
    },
    // Pre-compute formatted goals for all teams to avoid recomputing in template loops
    formattedGoalsMap() {
      const result = {};
      const goalsWithFocusStats = this.goalsWithFocusStats || {};
      const goalsTeamAI = this.goalsTeamAI || {};

      // Get all team IDs we need to format
      const teamIds = new Set([
        ...Object.keys(goalsWithFocusStats),
        ...Object.keys(goalsTeamAI),
      ]);
      
      teamIds.forEach((teamId) => {
        if (goalsWithFocusStats[teamId]) {
          result[teamId] = goalsWithFocusStats[teamId];
        } else if (goalsTeamAI[teamId]) {
          result[teamId] = goalsTeamAI[teamId];
        } else {
          result[teamId] = [];
        }
      });
      
      return result;
    },
    // Goal focus trend data for line chart visualization
    goalFocusTrendData() {
      const teamId = this.selectedTeam || 'All Teams';
      const goalsTeamAI = this.goalsTeamAI || {};
      return this.$store.getters["focus/getGoalFocusTrendData"](
        teamId,
        this.includeSubteams,
        goalsTeamAI,
        10 // Top 10 goals
      );
    },
    // Unmatched focus items grouped by text for display
    unmatchedFocusItems() {
      const teamId = this.selectedTeam || 'All Teams';
      return this.$store.getters["focus/getUnmatchedFocusItems"](
        teamId,
        this.includeSubteams
      );
    },
    // Ancestor goals grouped by team for the linking modal
    ancestorGoalsGrouped() {
      if (!this.linkingTeamId) return [];
      return this.$store.getters["goals/ancestorGoalsForTeam"](this.linkingTeamId);
    },
    // Ancestor goals for the editing team (used in Advanced tab)
    ancestorGoalsForEditingTeam() {
      if (!this.editingTeam || !this.editingTeam.key) return [];
      return this.$store.getters["goals/ancestorGoalsForTeam"](this.editingTeam.key);
    },
    // Emergent Goals computed properties
    emergentGoalsLoading() {
      return this.$store.getters["focus/emergentGoalsLoading"];
    },
    emergentGoalsResult() {
      return this.$store.getters["focus/emergentGoalsResult"];
    },
    selectedTeamName() {
      if (!this.selectedTeam || this.selectedTeam === 'All Teams') {
        return 'All Teams';
      }
      const team = this.filteredTeams.find(t => t.key === this.selectedTeam);
      return team ? team.name : this.selectedTeam;
    },
  },
  watch: {
    editingTeam(newVal) {
      if (newVal) {
        this.goalsText = newVal.goals || "";
        // Populate advanced goals with existing goals for this team
        const existingGoals = this.goalsTeamAI[newVal.key] || [];
        if (existingGoals.length > 0) {
          // Show existing goals (non-archived) for editing
          this.advancedGoals = existingGoals
            .filter((g) => !g.archived)
            .map((g) => ({
              goal_id: g.goal_id, // Keep ID for updating existing goals
              title: g.quick_preview || '',
              description: g.goal_description || '',
              parent_goal_ids: g.parent_goal_ids || [],
            }));
          // If all goals are archived, add an empty one
          if (this.advancedGoals.length === 0) {
            this.advancedGoals = [{ title: '', description: '', parent_goal_ids: [] }];
          }
          // Default to advanced mode when editing existing goals
          this.goalInputMode = 'advanced';
        } else {
          // No existing goals, start fresh
          this.goalInputMode = 'freeform';
          this.advancedGoals = [{ title: '', description: '', parent_goal_ids: [] }];
        }
      }
    },
    // Handle loading completion - merged from two watchers to prevent override
    loading(newVal) {
      if (!newVal) {
        // From first watcher: handle initial expand
        this.expandRetryCount = 0;
        this.$nextTick(this.attemptInitialExpand);

        // From second watcher: handle chart rendering
        if (this.goalFocusTrendData && this.goalFocusTrendData.datasets && this.goalFocusTrendData.datasets.length > 0) {
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.renderAppropriateChart();
            });
          });
        }
      }
    },
    // When includeSubteams toggle changes, clear local cache and trigger debounced reload
    includeSubteams() {
      // Clear local cache
      this.teamFocusStats = {};
      // Reload focus data for currently expanded teams
      this.reloadExpandedTeamsFocus();
      // Use debounced reload to prevent cascading API calls
      if (this.debouncedReloadData) {
        this.debouncedReloadData({ clearCache: true });
      }
    },
    // When selected team changes, load focus data for the new team
    selectedTeam(newVal) {
      if (newVal && newVal !== 'All Teams') {
        // Reset expand state for new team
        this.initialExpandDone = false;
        this.expandRetryCount = 0;
        this.expandedRowKeys = [];
        // Load focus data and use debounced reload for other data
        this.$nextTick(() => {
          this.loadFocusForTeam(newVal);
          this.attemptInitialExpand();
          // Use debounced reload to prevent cascading API calls
          if (this.debouncedReloadData) {
            this.debouncedReloadData({ includeTeamFocus: false });
          }
        });
      } else if (newVal === 'All Teams') {
        // Use debounced reload for all teams
        if (this.debouncedReloadData) {
          this.debouncedReloadData({ includeTeamFocus: false });
        }
      }
    },
    // Re-render goal trend chart when data changes
    goalFocusTrendData: {
      handler(newData) {
        if (newData && newData.datasets && newData.datasets.length > 0) {
          // Use double nextTick to ensure DOM is fully rendered
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.renderAppropriateChart();
            });
          });
        }
      },
      deep: true,
    },
  },
  async created() {
    // Initialize debounced reload function to prevent cascading API calls
    this.debouncedReloadData = _.debounce(this.reloadAllData, 150);

    this.loading = true;
    try {
      // Phase 1: Teams required first (other operations depend on team data)
      await this.$store.dispatch("team/getTeamsOptions", this.user);

      // Phase 2: Parallelize independent operations for ~35-45% faster initial load
      await Promise.all([
        this.$store.dispatch("goals/getGoalsTeamAI"),
        this.loadHistoricalData(),
        this.loadWeeklySummary(),
      ]);
    } catch (error) {
      console.error('Error loading goals data:', error);
    } finally {
      this.loading = false;
    }
  },
  mounted() {
    // Render goal trend chart when component mounts and data is available
    this.$nextTick(() => {
      if (this.goalFocusTrendData && this.goalFocusTrendData.datasets && this.goalFocusTrendData.datasets.length > 0) {
        this.renderAppropriateChart();
      }
    });
  },
  beforeDestroy() {
    // Clean up chart instances
    if (this.goalTrendChartInstance) {
      this.goalTrendChartInstance.destroy();
      this.goalTrendChartInstance = null;
    }
    if (this.goalTrendBarChartInstance) {
      this.goalTrendBarChartInstance.destroy();
      this.goalTrendBarChartInstance = null;
    }
  },
  methods: {
    // Sparkline helper methods for trend tooltips
    getSparklinePoints(data) {
      if (!data || data.length === 0) return '';
      const values = data.map(d => d.value);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const range = max - min || 1;
      
      return data.map((item, index) => {
        const x = index * 30;
        const y = 40 - ((item.value - min) / range) * 36 - 2;
        return `${x},${y}`;
      }).join(' ');
    },
    getSparklineY(value, data) {
      if (!data || data.length === 0) return 20;
      const values = data.map(d => d.value);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const range = max - min || 1;
      return 40 - ((value - min) / range) * 36 - 2;
    },
    /**
     * Render the appropriate chart based on the number of weeks
     * - 2+ weeks: Stacked area chart
     * - 1 week: Horizontal stacked bar chart
     */
    renderAppropriateChart() {
      const trendData = this.goalFocusTrendData;
      if (!trendData || !trendData.summary) return;

      if (trendData.summary.totalWeeks >= 2) {
        this.renderGoalTrendChart();
      } else {
        this.renderSingleWeekBarChart();
      }
    },
    /**
     * Render the goal focus trend line chart using Chart.js
     */
    renderGoalTrendChart() {
      if (!this.$refs.goalTrendChart) return;
      
      // Destroy existing chart instance
      if (this.goalTrendChartInstance) {
        this.goalTrendChartInstance.destroy();
        this.goalTrendChartInstance = null;
      }

      const trendData = this.goalFocusTrendData;
      if (!trendData || !trendData.labels || !trendData.datasets || trendData.datasets.length === 0) {
        return;
      }

      const ctx = this.$refs.goalTrendChart.getContext('2d');

      this.goalTrendChartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: trendData.labels,
          datasets: trendData.datasets.map((dataset) => ({
            label: dataset.label,
            data: dataset.data,
            absoluteData: dataset.absoluteData, // For tooltip display
            borderColor: dataset.borderColor,
            backgroundColor: dataset.backgroundColor,
            fill: dataset.fill,
            tension: dataset.tension,
            pointRadius: dataset.pointRadius,
            pointHoverRadius: dataset.pointHoverRadius,
            borderWidth: dataset.borderWidth,
          })),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 20,
                font: {
                  size: 11,
                  weight: '500',
                  family: 'Inter, sans-serif',
                },
                boxWidth: 8,
                boxHeight: 8,
                color: '#606266',
              },
            },
            tooltip: {
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              titleColor: '#303133',
              titleFont: {
                size: 13,
                weight: '600',
              },
              bodyColor: '#606266',
              bodyFont: {
                size: 12,
              },
              borderColor: '#e0e0e0',
              borderWidth: 1,
              padding: 12,
              boxPadding: 4,
              usePointStyle: true,
              itemSort: (a, b) => b.parsed.y - a.parsed.y, // Sort tooltip items by value
              callbacks: {
                title: (tooltipItems) => {
                  if (tooltipItems.length === 0) return '';
                  const weekLabel = tooltipItems[0].label;
                  const dataIndex = tooltipItems[0].dataIndex;
                  const weeklyTotals = trendData.weeklyTotals || [];
                  const total = weeklyTotals[dataIndex] || 0;
                  return weekLabel + ' · ' + total + ' focus items';
                },
                label: (context) => {
                  const label = context.dataset.label || '';
                  const percentage = context.parsed.y || 0;
                  const absoluteData = context.dataset.absoluteData || [];
                  const absolute = absoluteData[context.dataIndex] || 0;
                  return ' ' + label + ': ' + percentage + '% (' + absolute + ')';
                },
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              grid: {
                display: false,
              },
              ticks: {
                font: {
                  size: 11,
                },
                color: '#909399',
              },
            },
            y: {
              stacked: true,
              beginAtZero: true,
              max: 100,
              grid: {
                color: '#f0f0f0',
              },
              ticks: {
                font: {
                  size: 11,
                },
                color: '#909399',
                callback: (value) => value + '%',
              },
              title: {
                display: true,
                text: 'Share of Focus',
                font: {
                  size: 12,
                  weight: '600',
                },
                color: '#606266',
              },
            },
          },
        },
      });
    },
    /**
     * Render a horizontal stacked bar chart for single-week data
     */
    renderSingleWeekBarChart() {
      if (!this.$refs.goalTrendBarChart) return;

      // Destroy existing chart instance
      if (this.goalTrendBarChartInstance) {
        this.goalTrendBarChartInstance.destroy();
        this.goalTrendBarChartInstance = null;
      }

      const trendData = this.goalFocusTrendData;
      if (!trendData || !trendData.datasets || trendData.datasets.length === 0) {
        return;
      }

      const ctx = this.$refs.goalTrendBarChart.getContext('2d');
      const weekLabel = trendData.labels && trendData.labels[0] ? trendData.labels[0] : 'This Week';

      // Transform data for horizontal stacked bar
      // Each dataset becomes a segment in the single bar
      const datasets = trendData.datasets.map((dataset) => ({
        label: dataset.label,
        data: [dataset.data[0] || 0], // Single data point (percentage)
        absoluteValue: dataset.absoluteData ? dataset.absoluteData[0] : 0,
        backgroundColor: dataset.backgroundColor,
        borderColor: dataset.borderColor,
        borderWidth: 1,
        borderRadius: 0,
      }));

      this.goalTrendBarChartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: [weekLabel],
          datasets: datasets,
        },
        options: {
          indexAxis: 'y', // Horizontal bar
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 20,
                font: {
                  size: 11,
                  weight: '500',
                  family: 'Inter, sans-serif',
                },
                boxWidth: 8,
                boxHeight: 8,
                color: '#606266',
              },
            },
            tooltip: {
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              titleColor: '#303133',
              titleFont: {
                size: 13,
                weight: '600',
              },
              bodyColor: '#606266',
              bodyFont: {
                size: 12,
              },
              borderColor: '#e0e0e0',
              borderWidth: 1,
              padding: 12,
              boxPadding: 4,
              usePointStyle: true,
              callbacks: {
                title: () => {
                  const weeklyTotals = trendData.weeklyTotals || [];
                  const total = weeklyTotals[0] || 0;
                  return weekLabel + ' · ' + total + ' focus items';
                },
                label: (context) => {
                  const label = context.dataset.label || '';
                  const percentage = context.parsed.x || 0;
                  const absolute = context.dataset.absoluteValue || 0;
                  return ' ' + label + ': ' + percentage + '% (' + absolute + ')';
                },
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              beginAtZero: true,
              max: 100,
              grid: {
                display: false,
              },
              ticks: {
                font: {
                  size: 11,
                },
                color: '#909399',
                callback: (value) => value + '%',
              },
            },
            y: {
              stacked: true,
              grid: {
                display: false,
              },
              ticks: {
                font: {
                  size: 12,
                  weight: '600',
                },
                color: '#303133',
              },
            },
          },
        },
      });
    },
    attemptInitialExpand() {
      if (this.initialExpandDone) return;
      if (this.searchKeyword) return;
      
      // Limit retry attempts to prevent infinite loop
      const MAX_RETRIES = 10;
      if (this.expandRetryCount >= MAX_RETRIES) {
        return;
      }
      
      const roots = this.rootTeams;
      if (!Array.isArray(roots) || roots.length === 0) return;
      
      const tableRef = this.$refs.goalsTable;
      if (!tableRef) {
        // Table not mounted yet; try again next tick (with limit)
        this.expandRetryCount++;
        this.$nextTick(this.attemptInitialExpand);
        return;
      }
      
      const firstKey = roots[0].key;
      this.expandedRowKeys = [firstKey];
      if (typeof tableRef.toggleRowExpansion === "function") {
        tableRef.toggleRowExpansion(roots[0], true);
      }
      this.initialExpandDone = true;
      
      // Load focus data for the first expanded team
      this.loadFocusForTeam(firstKey);
    },
    loadChildren(tree, treeNode, resolve) {
      // Find child teams where parent matches this team's key
      const teams = this.filteredTeams;
      
      // Build a Set of parent IDs for O(1) hasChildren lookup
      const parentIds = new Set();
      for (const team of teams) {
        if (team.parent) {
          parentIds.add(team.parent);
        }
      }
      
      const children = [];
      for (const team of teams) {
        if (team.parent === tree.key) {
          children.push({
            ...team,
            hasChildren: parentIds.has(team.key),
          });
        }
      }
      
      // Lazy load focus data for the expanded team
      this.loadFocusForTeam(tree.key);
      
      // โหลดข้อมูลของ child teams ทั้งหมดอัตโนมัติ
      if (children.length > 0) {
        // ใช้ Promise.all เพื่อโหลดแบบ parallel (เร็วกว่า)
        Promise.all(
          children.map(child => this.loadFocusForTeam(child.key))
        ).catch(error => {
          console.error('Error loading focus data for child teams:', error);
        });
      }
      
      resolve(children);
    },

    /**
     * Handle date range change - reload historical data
     */
    async handleDateRangeChange(value) {
      await this.$store.dispatch("focus/setDateRange", { dateRange: value, reload: false });
      // Reload historical data with new date range
      await this.loadHistoricalData();
    },

    /**
     * Load historical focus data for the selected team
     */
    async loadHistoricalData() {
      try {
        await this.$store.dispatch("focus/loadHistoricalFocusDataForTeam", {
          teamId: this.selectedTeam,
          includeChildren: this.includeSubteams,
        });
      } catch (error) {
        console.error('Error loading historical focus data:', error);
      }
    },

    /**
     * Load weekly summary for week-over-week changes
     */
    async loadWeeklySummary() {
      try {
        await this.$store.dispatch("focus/loadWeeklySummaryForTeam", {
          teamId: this.selectedTeam || 'All Teams',
        });
      } catch (error) {
        console.error('Error loading weekly summary:', error);
      }
    },

    /**
     * Reload all data in parallel - used by debounced watchers to prevent cascading API calls
     * @param {Object} options - Options for reload
     * @param {boolean} options.includeTeamFocus - Whether to reload team focus data (default: true)
     * @param {boolean} options.clearCache - Whether to clear teamFocusStats cache (default: false)
     */
    async reloadAllData(options = {}) {
      const { includeTeamFocus = true, clearCache = false } = options;

      if (clearCache) {
        this.teamFocusStats = {};
      }

      const promises = [
        this.loadHistoricalData(),
        this.loadWeeklySummary(),
      ];

      if (includeTeamFocus && this.selectedTeam && this.selectedTeam !== 'All Teams') {
        promises.push(this.loadFocusForTeam(this.selectedTeam));
      }

      try {
        await Promise.all(promises);
      } catch (error) {
        console.error('Error reloading data:', error);
      }
    },

    /**
     * Load focus data for a specific team (lazy loading)
     */
    async loadFocusForTeam(teamId) {
      if (!teamId) return;

      // Check if already cached locally
      const cacheKey = `${teamId}_${this.includeSubteams ? 'with_children' : 'direct'}`;
      if (this.teamFocusStats[cacheKey]) {
        return;
      }

      try {
        // Dispatch the action to load focus data for this team
        await this.$store.dispatch("focus/loadFocusDataForTeam", {
          teamId,
          includeChildren: this.includeSubteams,
        });

        // Get the stats from the store and cache locally
        const stats = this.$store.getters["focus/getTeamAlignmentStats"](teamId, this.includeSubteams);
        if (stats) {
          this.$set(this.teamFocusStats, cacheKey, stats);
        }
      } catch (error) {
        console.error('Error loading focus data for team:', teamId, error);
      }
    },

    /**
     * Get focus stats for a team (from local cache)
     */
    getTeamFocusStats(teamId) {


      const cacheKey = `${teamId}_${this.includeSubteams ? 'with_children' : 'direct'}`;
      return this.teamFocusStats[cacheKey] || null;
    },

    /**
     * Check if focus data is loading for a team
     */
    isLoadingFocus(teamId) {
      const cacheKey = `${teamId}_${this.includeSubteams ? 'with_children' : 'direct'}`;
      return this.loadingTeamFocus[cacheKey] || false;
    },

    /**
     * Reload focus data for all expanded teams (called when toggle changes)
     */
    async reloadExpandedTeamsFocus() {
      // Load focus data for expanded teams
      for (const teamKey of this.expandedRowKeys) {
        await this.loadFocusForTeam(teamKey);
      }
      // Also load for root teams that are visible
      for (const team of this.rootTeams) {
        if (team.goals) {
          await this.loadFocusForTeam(team.key);
        }
      }
    },

    addGoals(team) {
      this.$store.dispatch("goals/openEditModal", team.key);
    },

    editGoals(team) {
      this.$store.dispatch("goals/openEditModal", team.key);
    },

    cancelEdit() {
      this.goalsText = "";
      this.goalInputMode = 'freeform';
      this.advancedGoals = [{ title: '', description: '', parent_goal_ids: [] }];
      this.$store.dispatch("goals/closeEditModal");
    },

    /**
     * Add a new empty goal entry in advanced mode
     */
    addAdvancedGoal() {
      this.advancedGoals.push({ title: '', description: '', parent_goal_ids: [] });
    },

    /**
     * Remove a goal entry from advanced mode by index
     */
    removeAdvancedGoal(index) {
      if (this.advancedGoals.length > 1) {
        this.advancedGoals.splice(index, 1);
      }
    },

    async saveGoals() {
      if (!this.editingTeam) return;

      this.saving = true;

      try {
        let result;
        
        if (this.goalInputMode === 'advanced') {
          // Advanced mode: save structured goals directly
          const validGoals = this.advancedGoals.filter(g => g.title && g.title.trim() !== '');

          if (validGoals.length === 0) {
            this.$message({
              type: "warning",
              message: "Please add at least one goal with a title",
            });
            this.saving = false;
            return;
          }

          const tooLong = validGoals.find(g => g.title.length > 30);
          if (tooLong) {
            this.$message({
              type: "warning",
              message: "Goal titles must be 30 characters or less",
            });
            this.saving = false;
            return;
          }
          
          result = await this.$store.dispatch("goals/saveStructuredGoals", {
            teamId: this.editingTeam.key,
            goals: validGoals,
          });
          
          if (result.success) {
            // Build success message based on what was done
            let message = 'Goals saved successfully';
            if (result.goalsUpdated > 0 && result.goalsAdded > 0) {
              message = `${result.goalsUpdated} goal${result.goalsUpdated > 1 ? 's' : ''} updated, ${result.goalsAdded} added`;
            } else if (result.goalsUpdated > 0) {
              message = `${result.goalsUpdated} goal${result.goalsUpdated > 1 ? 's' : ''} updated`;
            } else if (result.goalsAdded > 0) {
              message = `${result.goalsAdded} goal${result.goalsAdded > 1 ? 's' : ''} added`;
            }
            this.$message({
              type: "success",
              message,
            });
            this.advancedGoals = [{ title: '', description: '' }];
          }
        } else {
          // Freeform mode: use existing AI-assisted save
          result = await this.$store.dispatch("goals/saveTeamGoals", {
            teamId: this.editingTeam.key,
            goals: this.goalsText,
          });

          if (result.success) {
            this.$message({
              type: "success",
              message: this.$t("goals.save_success"),
            });
            this.goalsText = "";
          }
        }
        
        if (!result.success) {
          this.$message({
            type: "error",
            message: this.$t("goals.save_error"),
          });
        }
      } catch (error) {
        console.error("Error saving goals:", error);
        this.$message({
          type: "error",
          message: this.$t("goals.save_error"),
        });
      } finally {
        this.saving = false;
      }
    },

    teamHasGoals(teamKey) {
      if (!teamKey) return false;
      // Check both data sources: legacy team.goals string and goalsTeamAI from Firestore
      const team = this.filteredTeams.find(t => t.key === teamKey);
      if (team && team.goals && team.goals.trim().length > 0) return true;
      const aiGoals = this.goalsTeamAI[teamKey];
      if (aiGoals && Array.isArray(aiGoals) && aiGoals.filter(g => !g.archived).length > 0) return true;
      return false;
    },

    formatGoals(teamId) {

      if (!teamId) return [];
      
      // Get base goals from pre-computed map
      let baseGoals = this.formattedGoalsMap[teamId] || [];

      // Filter based on showArchived toggle
      if (!this.showArchived) {
        baseGoals = baseGoals.filter(goal => !goal.archived);
      }
      
      // Get focus stats for this team from local cache
      const focusStats = this.getTeamFocusStats(teamId);

      if (!focusStats || !focusStats.goalStats) {
        return baseGoals;
      }

      // Enrich goals with focus stats from the team-scoped data
      return baseGoals.map((goal) => {
        const goalFocusStats = focusStats.goalStats[goal.goal_id];

        
        if (goalFocusStats) {
          return {
            ...goal,
            focusStats: {
              peopleCount: goalFocusStats.peopleCount || 0,
              openCount: goalFocusStats.openCount || 0,
              doneCount: goalFocusStats.doneCount || 0,
              totalItems: goalFocusStats.totalItems || 0,
              people: goalFocusStats.people || [],
            },
          };
        }
        return {
          ...goal,
          focusStats: {
            peopleCount: 0,
            openCount: 0,
            doneCount: 0,
            totalItems: 0,
            people: [],
          },
        };
      });
    },
    getGoalFocusStats(goalId) {
      if (!goalId || !this.goalAlignmentStats) return null;
      return this.goalAlignmentStats[goalId] || null;
    },

    /**
     * Get the chart color for a goal (to match table dots with chart)
     * Falls back to default purple if no color mapping exists
     */
    getGoalColor(goalId) {
      const trendData = this.goalFocusTrendData;
      if (trendData && trendData.goalColorMap && trendData.goalColorMap[goalId]) {
        return trendData.goalColorMap[goalId];
      }
      // Default color for goals not in the chart (e.g., no focus data yet)
      return '#6C30CC';
    },

    /**
     * Get initials from a full name
     */
    getInitials(fullname) {
      if (!fullname) return '?';
      const parts = fullname.trim().split(/\s+/);
      if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
      }
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },

    /**
     * Format number with commas for thousands
     */
    formatNumber(num) {
      if (num === null || num === undefined) return '0';
      return num.toLocaleString();
    },

    /**
     * Start inline editing for a goal
     */
    startEditGoal(goal, teamId, event) {
      // Prevent collapse toggle when clicking edit
      if (event) {
        event.stopPropagation();
      }
      this.editingGoalId = goal.goal_id;
      this.editingTeamIdForGoal = teamId;
      this.editQuickPreview = goal.quick_preview || '';
      this.editDescription = goal.goal_description || '';
      this.originalQuickPreview = goal.quick_preview || '';
      this.originalDescription = goal.goal_description || '';
    },

    /**
     * Cancel inline editing and revert to original values
     */
    cancelEditGoal() {
      this.editingGoalId = null;
      this.editingTeamIdForGoal = null;
      this.editQuickPreview = '';
      this.editDescription = '';
      this.originalQuickPreview = '';
      this.originalDescription = '';
    },

    /**
     * Check if a goal is currently being edited
     */
    isEditingGoal(goalId) {
      return this.editingGoalId === goalId;
    },

    /**
     * Handle escape key to cancel editing
     */
    handleEditKeydown(event) {
      if (event.key === 'Escape') {
        this.cancelEditGoal();
      }
    },

    /**
     * Save the edited goal with confirmation dialog
     */
    async confirmSaveGoal() {
      // Check if anything actually changed
      const hasChanges = 
        this.editQuickPreview !== this.originalQuickPreview ||
        this.editDescription !== this.originalDescription;

      if (!hasChanges) {
        this.cancelEditGoal();
        return;
      }

      if (this.editQuickPreview.length > 30) {
        this.$message({
          type: "warning",
          message: "Goal title must be 30 characters or less",
        });
        return;
      }

      try {
        await this.$confirm(
          'Are you sure you want to save changes to this goal?',
          'Confirm Changes',
          {
            confirmButtonText: 'Save',
            cancelButtonText: 'Cancel',
            type: 'warning',
          }
        );

        // User confirmed, proceed with save
        await this.saveEditedGoal();
      } catch {
        // User cancelled the confirmation dialog, stay in edit mode
        // Do nothing - keep the edit state active
      }
    },

    /**
     * Actually save the edited goal to Firestore
     */
    async saveEditedGoal() {
      if (!this.editingGoalId || !this.editingTeamIdForGoal) return;

      this.savingGoal = true;

      try {
        const result = await this.$store.dispatch("goals/updateSingleGoal", {
          teamId: this.editingTeamIdForGoal,
          goalId: this.editingGoalId,
          quick_preview: this.editQuickPreview,
          goal_description: this.editDescription,
        });

        if (result.success) {
          this.$message({
            type: "success",
            message: "Goal updated successfully",
          });
          this.cancelEditGoal();
        } else {
          this.$message({
            type: "error",
            message: "Failed to update goal",
          });
        }
      } catch (error) {
        console.error("Error updating goal:", error);
        this.$message({
          type: "error",
          message: "Failed to update goal",
        });
      } finally {
        this.savingGoal = false;
      }
    },

    /**
     * Archive a goal with confirmation dialog
     */
    async confirmArchiveGoal(goal, teamId, event) {
      if (event) {
        event.stopPropagation();
      }

      try {
        await this.$confirm(
          'Are you sure you want to archive this goal? It can be restored later from the "Show Archived" view.',
          'Archive Goal',
          {
            confirmButtonText: 'Archive',
            cancelButtonText: 'Cancel',
            type: 'warning',
          }
        );

        // User confirmed, proceed with archive
        await this.archiveGoal(goal, teamId);
      } catch {
        // User cancelled the confirmation dialog
      }
    },

    /**
     * Archive a goal
     */
    async archiveGoal(goal, teamId) {
      this.archivingGoal = true;

      try {
        const result = await this.$store.dispatch("goals/archiveGoal", {
          teamId: teamId,
          goalId: goal.goal_id,
        });

        if (result.success) {
          this.$message({
            type: "success",
            message: "Goal archived successfully",
          });
        } else {
          this.$message({
            type: "error",
            message: "Failed to archive goal",
          });
        }
      } catch (error) {
        console.error("Error archiving goal:", error);
        this.$message({
          type: "error",
          message: "Failed to archive goal",
        });
      } finally {
        this.archivingGoal = false;
      }
    },

    /**
     * Unarchive a goal with confirmation dialog
     */
    async confirmUnarchiveGoal(goal, teamId, event) {
      if (event) {
        event.stopPropagation();
      }

      try {
        await this.$confirm(
          'Are you sure you want to restore this goal? It will be visible in the active goals list.',
          'Restore Goal',
          {
            confirmButtonText: 'Restore',
            cancelButtonText: 'Cancel',
            type: 'info',
          }
        );

        // User confirmed, proceed with unarchive
        await this.unarchiveGoal(goal, teamId);
      } catch {
        // User cancelled the confirmation dialog
      }
    },

    /**
     * Unarchive a goal
     */
    async unarchiveGoal(goal, teamId) {
      this.archivingGoal = true;

      try {
        const result = await this.$store.dispatch("goals/unarchiveGoal", {
          teamId: teamId,
          goalId: goal.goal_id,
        });

        if (result.success) {
          this.$message({
            type: "success",
            message: "Goal restored successfully",
          });
        } else {
          this.$message({
            type: "error",
            message: "Failed to restore goal",
          });
        }
      } catch (error) {
        console.error("Error restoring goal:", error);
        this.$message({
          type: "error",
          message: "Failed to restore goal",
        });
      } finally {
        this.archivingGoal = false;
      }
    },

    /**
     * Delete a goal with confirmation dialog
     */
    async confirmDeleteGoal(goal, teamId, event) {
      if (event) {
        event.stopPropagation();
      }

      try {
        await this.$confirm(
          'Are you sure you want to permanently delete this goal? This action cannot be undone.',
          'Delete Goal',
          {
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            type: 'error',
            confirmButtonClass: 'el-button--danger',
          }
        );

        // User confirmed, proceed with delete
        await this.deleteGoal(goal, teamId);
      } catch {
        // User cancelled the confirmation dialog
      }
    },

    /**
     * Permanently delete a goal
     */
    async deleteGoal(goal, teamId) {
      this.deletingGoal = true;

      try {
        const result = await this.$store.dispatch("goals/deleteGoal", {
          teamId: teamId,
          goalId: goal.goal_id,
        });

        if (result.success) {
          this.$message({
            type: "success",
            message: "Goal deleted permanently",
          });
        } else {
          this.$message({
            type: "error",
            message: "Failed to delete goal",
          });
        }
      } catch (error) {
        console.error("Error deleting goal:", error);
        this.$message({
          type: "error",
          message: "Failed to delete goal",
        });
      } finally {
        this.deletingGoal = false;
      }
    },

    /**
     * Get parent goal details for tooltip/display
     * Uses the store getter to fetch full goal info
     */
    getParentGoalDetailsComputed(goalIds) {
      if (!goalIds || goalIds.length === 0) return [];
      return this.$store.getters["goals/getParentGoalDetails"](goalIds);
    },

    /**
     * Get tooltip content for the link icon
     */
    getParentGoalLinkTooltip(goal) {
      if (!goal.parent_goal_ids || goal.parent_goal_ids.length === 0) {
        return 'Link to parent team goals';
      }
      const count = goal.parent_goal_ids.length;
      return `Linked to ${count} parent goal${count > 1 ? 's' : ''} - click to edit`;
    },

    /**
     * Open the parent goal linking modal
     */
    openLinkingModal(goal, teamId, event) {
      if (event) {
        event.stopPropagation();
      }
      this.linkingGoal = { ...goal };
      this.linkingTeamId = teamId;
      // Pre-populate with existing links
      this.selectedParentGoalIds = goal.parent_goal_ids ? [...goal.parent_goal_ids] : [];
      this.linkingModalVisible = true;
    },

    /**
     * Close the parent goal linking modal
     */
    closeLinkingModal() {
      this.linkingModalVisible = false;
      this.linkingGoal = null;
      this.linkingTeamId = null;
      this.selectedParentGoalIds = [];
    },

    /**
     * Toggle a parent goal selection
     */
    toggleParentGoal(goalId) {
      const index = this.selectedParentGoalIds.indexOf(goalId);
      if (index === -1) {
        this.selectedParentGoalIds.push(goalId);
      } else {
        this.selectedParentGoalIds.splice(index, 1);
      }
    },

    /**
     * Save the parent goal links
     */
    async saveGoalLinks() {
      if (!this.linkingGoal || !this.linkingTeamId) return;

      this.savingLinks = true;

      try {
        const result = await this.$store.dispatch("goals/updateGoalParentLinks", {
          teamId: this.linkingTeamId,
          goalId: this.linkingGoal.goal_id,
          parentGoalIds: this.selectedParentGoalIds,
        });

        if (result.success) {
          const count = this.selectedParentGoalIds.length;
          this.$message({
            type: "success",
            message: count > 0 
              ? `Goal linked to ${count} parent goal${count > 1 ? 's' : ''}` 
              : "Parent goal links removed",
          });
          this.closeLinkingModal();
        } else {
          this.$message({
            type: "error",
            message: "Failed to update goal links",
          });
        }
      } catch (error) {
        console.error("Error saving goal links:", error);
        this.$message({
          type: "error",
          message: "Failed to update goal links",
        });
      } finally {
        this.savingLinks = false;
      }
    },

    // ==================== Emergent Goals Methods ====================

    /**
     * Open the emergent goals modal and load suggestions (cached or fresh)
     */
    async openEmergentGoalsModal() {
      this.emergentGoalsModalVisible = true;
      const teamId = this.selectedTeam || 'All Teams';

      // Always load focus data first - needed for modal to display properly
      await this.loadEmergentGoalsFocusData();

      // Compute allowed goal IDs so cached suggestions cannot bypass scoping rules
      const { allowedGoalIds } = this.getExistingGoalsForEmergentDiscovery(teamId);

      // Then try to load cached AI suggestions (valid for 24 hours)
      try {
        const cached = await this.$store.dispatch("focus/loadCachedEmergentGoalsSuggestions", {
          teamId,
          maxAgeHours: 24,
          allowedGoalIds,
        });

        if (cached) {
          // Cached suggestions will be set in the store by the action
          return;
        }
      } catch (error) {
        console.error('[Goals] Cache check error:', error);
      }

      // No valid cache, run AI discovery
      await this.discoverEmergentGoals();
    },

    /**
     * Load focus data for emergent goals analysis (14-day range)
     * Sets up emergentGoalsUnmatchedItems and emergentGoalsTotalFocusItems
     */
    async loadEmergentGoalsFocusData() {
      const teamId = this.selectedTeam || 'All Teams';

      // Calculate 14-day date range for emergent goals analysis
      const EMERGENT_GOALS_DAYS = 14;
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - EMERGENT_GOALS_DAYS);

      // Use 2-level depth limit for emergent goals discovery
      const MAX_DEPTH = 2;

      await this.$store.dispatch("focus/loadHistoricalFocusDataForTeam", {
        teamId: teamId === 'All Teams' ? this.getRootTeamId() : teamId,
        includeChildren: this.includeSubteams || teamId === 'All Teams',
        startDate,
        endDate,
        forceRefresh: false, // Use cache if available
        maxDepth: MAX_DEPTH,
      });

      // Build the cache key (must match store's cache key format)
      const formatDate = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const effectiveTeamId = teamId === 'All Teams' ? this.getRootTeamId() : teamId;
      const includeChildren = this.includeSubteams || teamId === 'All Teams';
      const dateKey = `${formatDate(startDate)}_${formatDate(endDate)}`;
      const depthKey = `_depth${MAX_DEPTH}`;
      const cacheKey = `${effectiveTeamId || 'all'}_${dateKey}_${includeChildren ? 'with_children' : 'direct'}${depthKey}`;

      // Get unmatched items from the 14-day cache
      const unmatchedItems = this.$store.getters["focus/getUnmatchedFocusItems"](
        effectiveTeamId,
        includeChildren,
        cacheKey
      );

      // Determine whether there are any in-scope (non-ancestor) goals for this selection
      const { existingGoals } = this.getExistingGoalsForEmergentDiscovery(teamId);
      const inScopeGoalsCount = (existingGoals || []).filter(g => !g.isAncestor).length;
      this.emergentGoalsDiscoveryNoGoalsInScope = inScopeGoalsCount === 0;

      // Default to standard unmatched items
      this.emergentGoalsDiscoveryUsesUnassigned = false;
      let itemsForDiscovery = unmatchedItems;

      // Fallback: if no unmatched items AND no in-scope goals exist, use unassigned (goal_id === null) items
      if ((!itemsForDiscovery || itemsForDiscovery.length === 0) && this.emergentGoalsDiscoveryNoGoalsInScope) {
        const unassignedItems = this.$store.getters["focus/getUnassignedFocusItemsForDiscovery"](
          effectiveTeamId,
          includeChildren,
          cacheKey
        );
        if (unassignedItems && unassignedItems.length > 0) {
          this.emergentGoalsDiscoveryUsesUnassigned = true;
          itemsForDiscovery = unassignedItems;
        }
      }

      // Store items for discovery / team attribution in modal
      this.emergentGoalsUnmatchedItems = itemsForDiscovery;

      // Get total focus items count for percentage calculation
      this.emergentGoalsTotalFocusItems = this.$store.getters["focus/getTotalFocusItemsCount"](cacheKey);
    },

    /**
     * Discover emergent goals using AI (always fresh, saves to cache)
     * Uses only the last 14 days of focus items for analysis
     */
    async discoverEmergentGoals() {
      const teamId = this.selectedTeam || 'All Teams';

      // Reload focus data with forceRefresh for fresh AI analysis
      await this.loadEmergentGoalsFocusData();

      // Use the already-loaded unmatched items
      const unmatchedItems = this.emergentGoalsUnmatchedItems;

      if (!unmatchedItems || unmatchedItems.length === 0) {
        this.$message({
          type: "info",
          message: this.emergentGoalsDiscoveryNoGoalsInScope
            ? "No unassigned focus items to analyze in the last 14 days"
            : "No unmatched focus items to analyze in the last 14 days",
        });
        return;
      }

      // Prepare unmatched items for the API
      const formattedItems = unmatchedItems.map(item => ({
        itemText: item.itemText,
        description: item.description || '',
        item_ids: item.item_ids || [item.item_id],
        count: item.count || 1,
        people: (item.people || []).map(p => ({
          email: p.email,
          fullname: p.fullname,
        })),
      }));

      // Get existing goals within scoped subtree + explicitly linked parent goals
      const { existingGoals } = this.getExistingGoalsForEmergentDiscovery(teamId);

      try {
        // discoverEmergentGoals now saves to cache internally before returning
        // This ensures suggestionId is available when autoApply runs
        await this.$store.dispatch("focus/discoverEmergentGoals", {
          teamId,
          unmatchedFocusItems: formattedItems,
          existingGoals,
        });
      } catch (error) {
        console.error("Error discovering emergent goals:", error);
        this.$message({
          type: "error",
          message: "Failed to analyze focus items",
        });
      }
    },

    /**
     * Get the first root team ID (for 'All Teams' selection)
     */
    getRootTeamId() {
      const teams = this.$store.getters["team/teamsall"] || [];
      const rootTeams = teams.filter(t => !t.parent || t.parent === 'root');
      return rootTeams.length > 0 ? rootTeams[0].key : null;
    },

    /**
     * Build existing goals list for Emergent Goals discovery with strict scoping.
     *
     * Rules:
     * - Candidate goals are limited to the selected team subtree (depth<=2 when includeSubteams is on)
     * - PLUS any goals explicitly linked via parent_goal_ids from in-scope goals (one-hop)
     * - Never include unrelated teams' goals
     */
    getExistingGoalsForEmergentDiscovery(teamId) {
      const goalsTeamAI = this.goalsTeamAI || {};
      const allTeams = this.$store.getters["team/teamsall"] || [];

      // Helper: team name lookup (always from full team list)
      const teamNameById = new Map();
      allTeams.forEach(t => {
        if (t && t.key) teamNameById.set(t.key, t.name || t.key);
      });
      const getTeamName = (tId) => teamNameById.get(tId) || tId;

      // Special case: "All Teams" => allow all active goals
      if (!teamId || teamId === 'All Teams') {
        const existingGoals = [];
        const allowedGoalIds = [];
        Object.keys(goalsTeamAI).forEach(tId => {
          const teamGoals = goalsTeamAI[tId] || [];
          teamGoals
            .filter(g => !g.archived)
            .forEach(g => {
              existingGoals.push({
                goal_id: g.goal_id,
                quick_preview: g.quick_preview,
                goal_description: g.goal_description || '',
                teamId: tId,
                teamName: getTeamName(tId),
                isAncestor: false,
              });
              allowedGoalIds.push(g.goal_id);
            });
        });
        return { existingGoals, allowedGoalIds };
      }

      // Build child index for BFS traversal (parent -> children[])
      const childrenByParent = {};
      allTeams.forEach(t => {
        if (t && t.parent) {
          if (!childrenByParent[t.parent]) childrenByParent[t.parent] = [];
          childrenByParent[t.parent].push(t.key);
        }
      });

      // Build team lookup for ancestor traversal
      const teamById = new Map();
      allTeams.forEach(t => {
        if (t && t.key) teamById.set(t.key, t);
      });

      // Determine in-scope teams: selected team + descendants (depth<=2 if includeSubteams is on)
      const MAX_DEPTH = 2;
      const inScopeTeamIds = new Set([teamId]);
      if (this.includeSubteams) {
        const queue = [{ id: teamId, depth: 0 }];
        const visited = new Set([teamId]);
        while (queue.length > 0) {
          const { id, depth } = queue.shift();
          if (depth >= MAX_DEPTH) continue;
          const children = childrenByParent[id] || [];
          for (const childId of children) {
            if (!childId || visited.has(childId)) continue;
            visited.add(childId);
            inScopeTeamIds.add(childId);
            queue.push({ id: childId, depth: depth + 1 });
          }
        }
      }

      // Collect all ancestor team IDs up the chain (unlimited layers)
      const ancestorTeamIds = [];
      const visitedAncestors = new Set();
      let current = teamById.get(teamId);
      while (current && current.parent && current.parent !== 'root') {
        const parentId = current.parent;
        if (!parentId || visitedAncestors.has(parentId)) break;
        visitedAncestors.add(parentId);
        ancestorTeamIds.push(parentId);
        current = teamById.get(parentId);
      }

      // Build a map of all active goals by goal_id for resolving linked parent goals
      const goalById = new Map();
      Object.keys(goalsTeamAI).forEach(tId => {
        (goalsTeamAI[tId] || [])
          .filter(g => !g.archived)
          .forEach(g => {
            if (g && g.goal_id) {
              goalById.set(g.goal_id, { ...g, teamId: tId });
            }
          });
      });

      const existingGoals = [];
      const allowedGoalIdsSet = new Set();

      // 1) Add in-scope goals
      const linkedParentGoalIds = new Set();
      for (const tId of inScopeTeamIds) {
        const teamGoals = goalsTeamAI[tId] || [];
        teamGoals
          .filter(g => !g.archived)
          .forEach(g => {
            if (!g || !g.goal_id) return;
            existingGoals.push({
              goal_id: g.goal_id,
              quick_preview: g.quick_preview,
              goal_description: g.goal_description || '',
              teamId: tId,
              teamName: getTeamName(tId),
              isAncestor: false,
            });
            allowedGoalIdsSet.add(g.goal_id);

            // Collect explicit parent goal links (one-hop)
            const parentIds = Array.isArray(g.parent_goal_ids) ? g.parent_goal_ids : [];
            parentIds.forEach(pid => {
              if (pid) linkedParentGoalIds.add(pid);
            });
          });
      }

      // 2) Add ancestor-team goals (all layers up the chain)
      ancestorTeamIds.forEach((tId) => {
        const teamGoals = goalsTeamAI[tId] || [];
        teamGoals
          .filter(g => !g.archived)
          .forEach(g => {
            if (!g || !g.goal_id) return;
            if (allowedGoalIdsSet.has(g.goal_id)) return;
            existingGoals.push({
              goal_id: g.goal_id,
              quick_preview: g.quick_preview,
              goal_description: g.goal_description || '',
              teamId: tId,
              teamName: getTeamName(tId),
              isAncestor: true,
            });
            allowedGoalIdsSet.add(g.goal_id);
          });
      });

      // 3) Add explicitly linked parent goals (may be outside ancestry chain)
      linkedParentGoalIds.forEach(parentGoalId => {
        if (!parentGoalId || allowedGoalIdsSet.has(parentGoalId)) return;
        const parentGoal = goalById.get(parentGoalId);
        if (!parentGoal) return;
        existingGoals.push({
          goal_id: parentGoal.goal_id,
          quick_preview: parentGoal.quick_preview,
          goal_description: parentGoal.goal_description || '',
          teamId: parentGoal.teamId,
          teamName: getTeamName(parentGoal.teamId),
          isAncestor: true, // treat linked parent goals as outside-subtree context
        });
        allowedGoalIdsSet.add(parentGoal.goal_id);
      });

      return { existingGoals, allowedGoalIds: Array.from(allowedGoalIdsSet) };
    },

    /**
     * Close the emergent goals modal
     */
    closeEmergentGoalsModal() {
      this.emergentGoalsModalVisible = false;
      // Don't invalidate cache on close - cache remains valid for 24 hours
      // Cache will be invalidated when significant changes happen (new goal created, etc.)
      this.$store.commit("focus/clearEmergentGoalsResult");
    },

    /**
     * Handle when an assignment suggestion is accepted (low confidence manual)
     */
    async onAssignmentAccepted({ index, suggestion }) {
      // Refresh focus data to show updated alignments
      await this.refreshFocusData();
      // Don't invalidate cache here - accepting an assignment doesn't change
      // what goals are available, so cached suggestions remain valid
      this.$message({
        type: "success",
        message: `Focus items assigned to "${suggestion.suggestedGoalTitle}"`,
      });
    },

    /**
     * Handle when multiple assignments are auto-applied (medium/high confidence)
     */
    async onAssignmentsAutoApplied({ count, assignments }) {
      // Refresh focus data to show updated alignments
      await this.refreshFocusData();
      // Don't invalidate cache here - we'll do it when modal closes
      // This allows re-opening the modal quickly without re-running AI
    },

    /**
     * Handle when a new goal is created from emergent goals
     */
    async onGoalCreated({ index, goal, teamId }) {
      // Refresh both goals and focus data
      await Promise.all([
        this.$store.dispatch("goals/getGoalsTeamAI"),
        this.refreshFocusData(),
      ]);
      // Invalidate cache since goals and focus items have changed
      const currentTeamId = teamId || this.selectedTeam || 'All Teams';
      await this.$store.dispatch("focus/invalidateEmergentGoalsCache", { teamId: currentTeamId });
    },

    /**
     * Refresh focus data after changes
     * Uses forceRefresh to bypass cache without clearing existing data
     * This prevents the view from going blank while loading new data
     */
    async refreshFocusData() {
      const teamId = this.selectedTeam || 'All Teams';

      // Use forceRefresh to bypass cache instead of clearing it
      // This keeps the view populated while new data loads
      if (teamId && teamId !== 'All Teams') {
        await this.$store.dispatch("focus/loadHistoricalFocusDataForTeam", {
          teamId,
          includeChildren: this.includeSubteams,
          forceRefresh: true, // Bypass cache without clearing existing data
        });
      } else {
        // For "All Teams", load data for the root/company level
        // Use the first team's data or load all focus data
        const teams = this.$store.getters["team/teamsall"] || [];
        if (teams.length > 0) {
          // Find root teams (teams without parent or parent is 'root')
          const rootTeams = teams.filter(t => !t.parent || t.parent === 'root');
          if (rootTeams.length > 0) {
            await this.$store.dispatch("focus/loadHistoricalFocusDataForTeam", {
              teamId: rootTeams[0].key,
              includeChildren: true, // Include all children for root team
              forceRefresh: true, // Bypass cache without clearing existing data
            });
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.margin-subpages {
  margin: 0 24px;
}

/* Header Controls Layout */
.header-controls {
  padding: 24px 0 16px 0;
}

.header-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-row-primary {
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-left .team-select {
  width: 200px;
}

.header-left .search-input {
  width: 200px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-right .date-range-select {
  width: 150px;
}

.center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.flex-center {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Toggle Switch Styles */
.toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 0;
  white-space: nowrap;
}

.toggle-label {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  user-select: none;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
}

.archived-count-badge {
  font-size: 11px;
  font-weight: 600;
  color: #909399;
  background: #f4f4f5;
  padding: 1px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
}

/* Date Range Selector Styles */
.date-range-select {
  width: 150px;
}

.date-range-badge {
  font-size: 11px;
  font-weight: 500;
  color: #6C30CC;
  background: rgba(108, 48, 204, 0.1);
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: 8px;
}

.loading-indicator {
  color: #6C30CC;
  font-size: 14px;
  margin-left: 8px;
}

.highlights {
  width: 896px;
  overflow: hidden;
}

/* Goal Trends Section */
.goal-trends-section {
  margin-top: 24px;
  margin-bottom: 24px;
}

.section-header {
  margin-bottom: 12px;
}

.section-subtitle {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.trend-summary-badge {
  font-size: 11px;
  font-weight: 500;
  color: #6C30CC;
  background: rgba(108, 48, 204, 0.1);
  padding: 2px 10px;
  border-radius: 10px;
}

.skipped-weeks-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #909399;
  background: rgba(144, 147, 153, 0.1);
  padding: 2px 10px;
  border-radius: 10px;
  cursor: help;
  
  i {
    font-size: 12px;
  }
}

.split-chart {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow:
    0px 1px 4px rgba(0, 0, 0, 0.04),
    0px 2px 8px rgba(0, 0, 0, 0.04);
}

.chart-container {
  background: white;
  position: relative;
}

.single-week-bar-container {
  background: white;
  position: relative;
  height: 120px;
  padding: 20px;
}

.chart-footer {
  padding: 12px 20px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
}

.unmatched-note {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.unmatched-breakdown {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.unmatched-breakdown-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.unmatched-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #909399;
  flex-shrink: 0;
}

.unmatched-dot-no-value {
  background: #757575; /* Material Gray 600 - darkest for explicitly no value */
}

.unmatched-dot-unknown {
  background: #BDBDBD; /* Material Gray 400 - medium for unknown/not set */
}

.unmatched-dot-has-value {
  background: #E0E0E0; /* Material Gray 300 - lightest for has value */
  border: 1px solid #BDBDBD;
}

.fc-757575 {
  color: #757575;
}

.fs-11 {
  font-size: 11px;
}

/* Expandable Unmatched Items Section */
.unmatched-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 4px 0;
  transition: background-color 0.2s;
  border-radius: 4px;
}

.unmatched-header:hover {
  background-color: rgba(108, 48, 204, 0.04);
}

.unmatched-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.unmatched-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.discover-goals-btn {
  background: linear-gradient(135deg, #6C30CC 0%, #9B59B6 100%);
  border: none;
  font-weight: 500;

  &:hover {
    background: linear-gradient(135deg, #5a28a8 0%, #8e44ad 100%);
  }
}

.unmatched-toggle i {
  font-size: 12px;
  transition: transform 0.2s;
}

.fc-6C30CC {
  color: #6C30CC;
}

/* Expand/Collapse Animation */
.unmatched-expand-enter-active,
.unmatched-expand-leave-active {
  transition: all 0.3s ease-out;
  overflow: hidden;
}

.unmatched-expand-enter,
.unmatched-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.unmatched-expand-enter-to,
.unmatched-expand-leave {
  opacity: 1;
  max-height: 500px;
}

.unmatched-items-container {
  margin-top: 16px;
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden; /* Prevent horizontal scroll */
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #ffffff;
}

.unmatched-list {
  padding: 8px 0;
}

.unmatched-item-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  padding: 14px 20px;
  gap: 16px;
  border-bottom: 1px solid #f5f5f5;
  box-sizing: border-box;
}

.unmatched-item-row:last-child {
  border-bottom: none;
}

.unmatched-item-row.has-value {
  background: rgba(224, 224, 224, 0.15); /* Light gray tint for items with value */
}

.unmatched-item-row.no-value {
  background: rgba(117, 117, 117, 0.08); /* Subtle dark gray tint for items with no value */
}

.unmatched-item-row.value-unknown {
  background: transparent; /* No tint for unknown - neutral state */
}

.unmatched-item-content {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.unmatched-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.unmatched-item-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.unmatched-item-indicator.no-value {
  background: #757575; /* Darkest gray - explicitly no value (value_id === 'unmatched') */
}

.unmatched-item-indicator.unknown {
  background: #BDBDBD; /* Medium gray - value not set (null/undefined) */
}

.unmatched-item-indicator.has-value {
  background: #E0E0E0; /* Lightest gray - has valid value_id */
  border: 1px solid #BDBDBD;
}

.value-badge {
  font-size: 10px;
  padding: 0 6px;
  height: 18px;
  line-height: 18px;
  flex-shrink: 0;
}

.value-badge.has-value {
  background: #E8F5E9; /* Light green tint */
  border-color: #C8E6C9;
  color: #388E3C;
}

.value-badge.no-value {
  background: #FAFAFA;
  border-color: #9E9E9E;
  color: #616161;
}

.unmatched-item-text {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.unmatched-item-description {
  font-size: 12px;
  font-weight: 400;
  color: #909399;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.unmatched-item-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  max-width: 220px;
  margin-top: 2px; /* Align with first line of text */
}

.unmatched-people-count {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #6C30CC;
  background: rgba(108, 48, 204, 0.08);
  padding: 4px 10px;
  border-radius: 12px;
}

.unmatched-people-count i {
  font-size: 12px;
}

.unmatched-avatars-preview {
  display: flex;
  align-items: center;
}

.unmatched-person-name-label {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  margin-left: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.unmatched-single-person {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

.unmatched-mini-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  margin-left: -8px;
  border: 2px solid #fff;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

/* Reset margin for first avatar or single avatar */
.unmatched-mini-avatar:first-child,
.unmatched-single-person .unmatched-mini-avatar {
  margin-left: 0;
}

.unmatched-mini-avatar.more-count {
  background: #909399;
  font-size: 11px;
}

.ml-8 {
  margin-left: 8px;
}

.box-card-border {
  box-shadow:
    0px 0.3px 0.9px rgba(0, 0, 0, 0.008),
    0px 0.8px 2.1px rgba(0, 0, 0, 0.012),
    0px 1.5px 4px rgba(0, 0, 0, 0.015),
    0px 2.7px 7.1px rgba(0, 0, 0, 0.018),
    0px 5px 13.4px rgba(0, 0, 0, 0.022),
    0px 12px 32px rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  padding: 24px;
  width: 100%;
}

/* Summary Metrics */
.summary-metrics {
  display: flex;
  align-items: flex-start;
  gap: 40px;
}

.metric-block {
  min-width: 180px;
}

.metric-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.metric-item {
  display: flex;
  align-items: baseline;
}

.metric-separator {
  font-size: 24px;
  color: #dcdfe6;
  font-weight: 300;
}

.fs-28 {
  font-size: 28px;
}

.fs-32 {
  font-size: 32px;
}

.fw-600 {
  font-weight: 600;
}

.fc-909399 {
  color: #909399;
}

.fc-c0c4cc {
  color: #c0c4cc;
}

.ml-4 {
  margin-left: 4px;
}

/* Alignment Block */
.alignment-block {
  flex: 1;
  border-left: 1px solid #eee;
  padding-left: 40px;
}

.alignment-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.alignment-content {
  display: flex;
  align-items: center;
  gap: 32px;
}

.alignment-value {
  display: flex;
  align-items: baseline;
  min-width: 70px;
}

.alignment-bar-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.alignment-bar {
  width: 100%;
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.alignment-bar-fill {
  height: 100%;
  background: #3AC47D;
  border-radius: 4px;
  transition: width 0.5s ease, background 0.3s ease;
}

.alignment-stats {
  display: flex;
  gap: 16px;
  font-size: 12px;
  font-weight: 500;
}

.alignment-stats .stat {
  display: flex;
  align-items: center;
  gap: 6px;
}

.alignment-stats .stat.aligned {
  color: #3AC47D;
}

.alignment-stats .stat.unaligned {
  color: #909399;
}

.alignment-stats .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.alignment-stats .stat.aligned .dot {
  background: #3AC47D;
}

.alignment-stats .stat.unaligned .dot {
  background: #dcdfe6;
}

.alignment-stats-na {
  display: flex;
  align-items: center;
  font-style: italic;
}

/* Week-over-week change indicator styles (inline with main score) */
.label-change {
  font-size: 11px;
  font-weight: 700;
  margin-left: 6px;
}

.text-green {
  color: #3AC47D;
}

.text-red {
  color: #F44336;
}

.fc-6A6A6A {
  color: #6A6A6A;
}

.cursor-pointer {
  cursor: pointer;
}

.grid-4-20 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.flex-column {
  display: flex;
  flex-direction: column;
}

.flex-row {
  display: flex;
  flex-direction: row;
}

.modal-header {
  background-color: #f2f2f2;
  border-radius: 6px 6px 0 0;
  font-size: 20px;
  font-weight: 700;
  color: #4a4a4a;
}

/* Table Styles */
.team-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.team-color-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.team-name-text {
  font-weight: 500;
  color: #303133;
  font-size: 14px;
  line-height: 1.5;
}

.goals-display {
  padding: 4px 0;

  .goals-collapse {
    border: none;

    .el-collapse-item {
      border-bottom: 1px solid #f5f5f5;
      margin-bottom: 0;

      &:last-child {
        border-bottom: none;
      }

      &:first-child {
        margin-top: 0;
      }
    }
  }

  .goal-preview-header {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding-right: 12px;
    flex-wrap: wrap;
    row-gap: 6px;
  }

  .goal-bullet {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #6C30CC;
    flex-shrink: 0;
  }

  .goal-bullet.no-focus {
    background-color: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.2);
  }

  .goal-quick-text {
    flex: 1;
    min-width: 180px;
    font-size: 14px;
    font-weight: 500;
    color: #303133;
    line-height: 1.5;
  }

  .ai-badge {
    margin-left: 8px;
    font-size: 10px;
    padding: 2px 6px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: white;
    height: 18px;
    line-height: 14px;
  }

  .focus-stats-badge {
    margin-left: auto;
    font-size: 13px;
    padding: 5px 12px;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-radius: 16px;
    color: #059669;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  .focus-stats-badge i {
    font-size: 13px;
    color: #059669;
  }

  .focus-stats-badge .people-num {
    font-weight: 700;
    font-size: 14px;
  }

  .focus-stats-badge .stats-separator {
    color: #a7f3d0;
    margin: 0 4px;
  }

  .focus-stats-badge .items-count {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 13px;
  }

  .focus-stats-badge .open-num {
    color: #6C30CC;
    font-weight: 600;
  }

  .focus-stats-badge .items-slash {
    color: #94a3b8;
  }

  .focus-stats-badge .done-num {
    color: #3AC47D;
    font-weight: 600;
  }

  .no-focus-badge {
    margin-left: auto;
    font-size: 13px;
    padding: 5px 12px;
    background: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: 16px;
    color: #b45309;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .no-focus-badge i {
    font-size: 14px;
  }

  .loading-focus-badge {
    margin-left: auto;
    font-size: 14px;
    color: #6C30CC;
  }

  .people-avatars-preview {
    display: flex;
    align-items: center;
    margin-left: 8px;
    flex-shrink: 0;
  }

  .mini-avatar {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-size: cover;
    background-position: center;
    border: 2px solid white;
    margin-left: -6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 700;
    color: white;
    cursor: pointer;
    transition: transform 0.2s;
  }

  .mini-avatar:first-child {
    margin-left: 0;
  }

  .mini-avatar:hover {
    transform: scale(1.2);
    z-index: 20 !important;
  }

  .mini-avatar.more-count {
    background: #6C30CC;
    font-size: 9px;
  }

  .goal-people-list {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #eee;
  }

  .goal-people-header {
    font-size: 12px;
    font-weight: 600;
    color: #606266;
    margin-bottom: 12px;
  }

  .goal-people-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .person-chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 4px;
    background: #f7f7f9;
    border-radius: 20px;
    border: 1px solid #e8e8e8;
  }

  .person-chip-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    color: white;
    flex-shrink: 0;
  }

  .person-chip-name {
    font-size: 12px;
    font-weight: 500;
    color: #303133;
  }

  .goal-full-description {
    font-size: 14px;
    color: #606266;
    line-height: 1.6;
    padding: 12px 16px;
    margin-left: 28px;
    margin-top: 4px;
    margin-bottom: 8px;
    background: #fafafa;
    border-left: 3px solid #6C30CC;
    border-radius: 0 4px 4px 0;
  }

  /* Parent goal links section in expanded view */
  .goal-parent-links-section {
    margin-top: 20px;
    padding: 16px;
    background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
    border: 1px solid #e9d5ff;
    border-radius: 10px;
  }

  .parent-links-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .parent-links-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #7c3aed;
  }

  .parent-links-title i {
    font-size: 14px;
  }

  .edit-links-btn {
    font-size: 12px;
    font-weight: 600;
    padding: 6px 12px;
    color: #6C30CC;
    background: white;
    border: 1px solid #e9d5ff;
    border-radius: 6px;
    transition: all 0.15s ease;
  }

  .edit-links-btn:hover {
    color: white;
    background: #6C30CC;
    border-color: #6C30CC;
  }

  .parent-links-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .parent-link-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: white;
    border: 1px solid #e9d5ff;
    border-radius: 8px;
    font-size: 13px;
    box-shadow: 0 1px 3px rgba(124, 58, 237, 0.08);
    transition: all 0.15s ease;
  }

  .parent-link-item:hover {
    border-color: #c4b5fd;
    box-shadow: 0 2px 6px rgba(124, 58, 237, 0.12);
  }

  .parent-link-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  .parent-link-team {
    color: #6b7280;
    font-weight: 500;
    font-size: 12px;
  }

  .parent-link-arrow {
    color: #c4b5fd;
    font-size: 12px;
  }

  .parent-link-goal {
    color: #7c3aed;
    font-weight: 600;
    flex: 1;
  }

  .no-parent-links {
    padding: 14px 16px;
    background: white;
    border: 1px dashed #d8b4fe;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .no-links-text {
    font-size: 13px;
    color: #a78bfa;
  }

  /* Inline goal editing styles */
  .goal-edit-icon,
  .goal-link-icon,
  .goal-archive-icon,
  .goal-unarchive-icon,
  .goal-delete-icon {
    opacity: 0;
    cursor: pointer;
    color: #909399;
    font-size: 14px;
    padding: 4px;
    margin-left: 4px;
    transition: opacity 0.2s, color 0.2s;
    flex-shrink: 0;
  }

  .goal-preview-header:hover .goal-edit-icon,
  .goal-preview-header:hover .goal-link-icon,
  .goal-preview-header:hover .goal-archive-icon,
  .goal-preview-header:hover .goal-unarchive-icon,
  .goal-preview-header:hover .goal-delete-icon {
    opacity: 1;
  }

  .goal-edit-icon:hover {
    color: #6C30CC;
  }

  .goal-link-icon:hover {
    color: #409EFF;
  }

  .goal-link-icon.has-links {
    opacity: 1;
    color: #409EFF;
  }

  .goal-archive-icon:hover {
    color: #E6A23C;
  }

  .goal-unarchive-icon:hover {
    color: #67C23A;
  }

  .goal-delete-icon:hover {
    color: #F56C6C;
  }

  /* Parent goal links badge */
  .parent-links-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
    border: 1px solid #c4b5fd;
    border-radius: 14px;
    color: #7c3aed;
    font-size: 12px;
    font-weight: 700;
    margin-left: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
    box-shadow: 0 1px 3px rgba(124, 58, 237, 0.1);
  }

  .parent-links-badge:hover {
    background: linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 100%);
    border-color: #a78bfa;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(124, 58, 237, 0.2);
  }

  .parent-links-badge i {
    font-size: 12px;
  }

  /* Archived goal styles */
  .archived-text {
    color: #909399 !important;
    text-decoration: line-through;
    text-decoration-color: #c0c4cc;
  }

  .archived-badge {
    margin-left: 8px;
    background: #f4f4f5;
    border-color: #e9e9eb;
    color: #909399;
    font-size: 10px;
  }

  .goal-bullet.archived {
    background-color: #909399 !important;
    box-shadow: none !important;
  }

  .goal-preview-header.archived {
    opacity: 0.7;
  }

  .goal-preview-header.archived:hover {
    opacity: 1;
  }

  .goal-preview-header.editing {
    flex-wrap: wrap;
  }

  .goal-edit-header-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
  }

  .goal-edit-input {
    flex: 1;
    min-width: 150px;
    max-width: 350px;
  }

  .goal-edit-header-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
    align-items: center;
  }

  .goal-action-icon {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    padding: 0;
  }

  .goal-action-icon:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .goal-action-icon.cancel-icon {
    background: #f5f5f5;
    color: #909399;
  }

  .goal-action-icon.cancel-icon:hover:not(:disabled) {
    background: #fef0f0;
    color: #f56c6c;
  }

  .goal-action-icon.save-icon {
    background: #6C30CC;
    color: white;
  }

  .goal-action-icon.save-icon:hover:not(:disabled) {
    background: #823CF0;
  }

  .goal-edit-textarea {
    width: 100%;
  }

  .goal-edit-hint {
    font-size: 12px;
    color: #909399;
    margin-top: 8px;
    margin-bottom: 0;
  }
}

.no-goals-text {
  font-size: 13px;
  color: #909399;
  font-style: italic;
}

/* Action Buttons */
.action-btn {
  font-size: 13px;
  font-weight: 600;
  padding: 0;
  transition: all 0.2s;
}

.edit-action {
  color: #6C30CC;
}

.edit-action:hover {
  color: #823CF0;
}

.add-action {
  color: #6C30CC;
}

.add-action:hover {
  color: #823CF0;
}

.add-action i {
  margin-right: 4px;
}

/* Modal Styles - moved to lang="scss" section for proper scoping */

/* Utility Classes */
.fs-11 {
  font-size: 11px;
}

.fs-12 {
  font-size: 12px;
}

.fs-15 {
  font-size: 15px;
}

.fs-16 {
  font-size: 16px;
}

.fs-24 {
  font-size: 24px;
}

.fw-500 {
  font-weight: 500;
}

.fw-700 {
  font-weight: 700;
}

.mt-12 {
  margin-top: 12px;
}

.mt-20 {
  margin-top: 20px;
}

.mt-24 {
  margin-top: 24px;
}

.mb-12 {
  margin-bottom: 12px;
}

.mb-24 {
  margin-bottom: 24px;
}

.mb-40 {
  margin-bottom: 40px;
}

.mr-24 {
  margin-right: 24px;
}

.px-32 {
  padding-left: 32px;
  padding-right: 32px;
}

.py-24 {
  padding-top: 24px;
  padding-bottom: 24px;
}

.pb-24 {
  padding-bottom: 24px;
}

.pt-20 {
  padding-top: 20px;
}

.pb-40 {
  padding-bottom: 40px;
}

.blurelement {
  filter: blur(4px);
  user-select: none;
  pointer-events: none;
}

.blur-text {
  filter: blur(4px);
  user-select: none;
  pointer-events: none;
}

/* Blur dropdown text only - keeps interactive */
.blur-dropdown ::v-deep .el-input__inner {
  filter: blur(4px);
}


/* Skeleton Loading Styles */
.skeleton-label {
  width: 80px;
  height: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
}

.skeleton-value {
  width: 100px;
  height: 28px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
}

.table-row-skeleton {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  margin-bottom: 12px;
  background: #fff;
}

.skeleton-color-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  flex-shrink: 0;
}

.skeleton-team-name {
  width: 180px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
  flex-shrink: 0;
}

.skeleton-goals {
  flex: 1;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>

<style lang="scss">
#goalsContainer {
  padding-bottom: 40px;
  /* Table Styles */
  .goals-table {
    font-size: 14px;
  }

  .goals-table .el-table th {
    background: #fafafa;
    border-bottom: 2px solid #e0e0e0;
  }

  .goals-table .el-table th .cell {
    color: #2e2e2e;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .goals-table .el-table td,
  .goals-table .el-table th {
    padding: 14px 0;
  }

  .goals-table .el-table td {
    border-bottom: 1px solid #f0f0f0;
  }

  .goals-table .el-table__row {
    transition: background-color 0.2s;
  }

  .goals-table .el-table__row:hover {
    background-color: #fafafa;
  }

  .goals-table .el-table__expand-icon {
    color: #6C30CC;
    font-size: 14px;
  }

  .goals-table .el-table__expand-icon:hover {
    color: #823CF0;
  }

  .goals-table .el-table__body-wrapper.is-scrolling-none {
    min-height: auto !important;
  }

  /* Search and Select Styles */
  .team-select,
  .search-input {
    width: 100%;
  }

  .team-select .el-input__inner,
  .search-input .el-input__inner {
    border-radius: 4px;
  }

  /* Goals Collapse Component Overrides */
  .goal-collapse-item {
    .el-collapse-item__header {
      height: auto;
      line-height: 1.6;
      padding: 6px 0;
      border: none;
      background: transparent;
      font-size: 14px;

      &:hover {
        background: rgba(108, 48, 204, 0.03);
      }

      .el-collapse-item__arrow {
        color: #6C30CC;
        font-size: 12px;
        margin-right: 8px;
        transition: transform 0.3s;
      }
    }

    .el-collapse-item__wrap {
      border: none;
      background: transparent;
    }

    .el-collapse-item__content {
      padding-bottom: 0;
    }

    &.goal-archived {
      .el-collapse-item__header {
        background: #fafafa;
        
        &:hover {
          background: #f5f5f5;
        }
      }

      .el-collapse-item__arrow {
        color: #909399;
      }
    }
  }
}

/* Modal Dialog Styles */
#goalsModal {
  .el-dialog {
    border-radius: 6px;
  }

  .el-dialog__header {
    padding: 0px;
  }

  .el-dialog__body {
    padding: 0;
    color: #000;
  }

  .el-dialog__headerbtn {
    top: 10px;
  }

  .team-name-display {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
  }

  .team-color-tag {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .goals-textarea {
    font-size: 15px;

    textarea {
      font-size: 15px;
      line-height: 1.6;
      border-radius: 4px;
      border: 1px solid #dcdfe6;
      padding: 12px;
      transition: border-color 0.2s;

      &:focus {
        border-color: #6C30CC;
        outline: none;
      }
    }
  }

  .goals-hint {
    margin: 12px 0 0 0;
    font-size: 13px;
    color: #777777;
    line-height: 1.5;
  }

  /* Tabs Styling */
  .goals-input-tabs {
    .el-tabs__header {
      margin-bottom: 16px;
    }

    .el-tabs__nav-wrap::after {
      height: 1px;
      background: #e8e8e8;
    }

    .el-tabs__item {
      font-size: 14px;
      font-weight: 600;
      color: #606266;
      padding: 0 20px;
      height: 40px;
      line-height: 40px;

      &.is-active {
        color: #6C30CC;
      }

      &:hover {
        color: #6C30CC;
      }
    }

    .el-tabs__active-bar {
      background-color: #6C30CC;
      height: 2px;
    }
  }

  .tab-description {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #f8f9fa;
    border-radius: 6px;
    margin-bottom: 16px;
    font-size: 13px;
    color: #606266;
    line-height: 1.5;

    i {
      color: #6C30CC;
      font-size: 16px;
    }
  }

  /* Advanced Goals List */
  .advanced-goals-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-height: 400px;
    overflow-y: auto;
    padding-right: 4px;
  }

  .advanced-goal-item {
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 16px;
    transition: border-color 0.2s, box-shadow 0.2s;

    &:hover {
      border-color: #d0d0d0;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    }

    &:focus-within {
      border-color: #6C30CC;
      box-shadow: 0 0 0 2px rgba(108, 48, 204, 0.1);
    }
  }

  .goal-item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .goal-item-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .goal-number {
    font-size: 12px;
    font-weight: 700;
    color: #6C30CC;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .goal-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 10px;
    text-transform: uppercase;
    letter-spacing: 0.3px;

    i {
      font-size: 10px;
    }
  }

  .goal-status-badge.existing {
    background: #ecfdf5;
    color: #059669;
    border: 1px solid #a7f3d0;
  }

  .goal-status-badge.new {
    background: #eff6ff;
    color: #3b82f6;
    border: 1px solid #bfdbfe;
  }

  .advanced-goal-item.is-existing {
    border-color: #a7f3d0;
    background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  }

  .advanced-goal-item.is-new {
    border-color: #bfdbfe;
    background: linear-gradient(135deg, #f0f9ff 0%, #eff6ff 100%);
  }

  .remove-goal-btn {
    color: #909399;
    padding: 4px;
    font-size: 16px;

    &:hover {
      color: #F56C6C;
    }
  }

  .char-counter {
    text-align: right;
    font-size: 11px;
    color: #909399;
    margin-top: 4px;
    margin-bottom: 8px;
    transition: color 0.2s ease;

    &.at-limit {
      color: #F56C6C;
      font-weight: 600;
    }
  }

  .inline-char-counter {
    font-size: 11px;
    color: #909399;
    white-space: nowrap;
    margin-left: 8px;
    transition: color 0.2s ease;

    &.at-limit {
      color: #F56C6C;
      font-weight: 600;
    }
  }

  .goal-title-input {
    margin-bottom: 4px;

    .el-input__inner {
      font-size: 15px;
      font-weight: 500;
      border-radius: 4px;
      border: 1px solid #dcdfe6;
      padding: 10px 12px;
      height: auto;

      &:focus {
        border-color: #6C30CC;
      }

      &::placeholder {
        color: #c0c4cc;
      }
    }
  }

  .goal-description-input {
    textarea {
      font-size: 14px;
      line-height: 1.5;
      border-radius: 4px;
      border: 1px solid #dcdfe6;
      padding: 10px 12px;

      &:focus {
        border-color: #6C30CC;
        outline: none;
      }

      &::placeholder {
        color: #c0c4cc;
      }
    }
  }

  .add-goal-btn {
    margin-top: 16px;
    padding: 0;
    color: #6C30CC;
    font-size: 14px;
    font-weight: 600;

    i {
      margin-right: 6px;
    }

    &:hover {
      color: #823CF0;
    }
  }

  /* Parent Goal Linking in Advanced Tab */
  .goal-parent-linking {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px dashed #e5e7eb;
  }

  .goal-parent-linking.no-parents {
    border-top: none;
    padding-top: 10px;
    margin-top: 10px;
  }

  .parent-linking-label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
    font-size: 12px;
    font-weight: 600;
    color: #7c3aed;

    i {
      font-size: 13px;
    }
  }

  .linked-count {
    font-size: 10px;
    font-weight: 700;
    background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
    color: #7c3aed;
    padding: 2px 8px;
    border-radius: 10px;
    margin-left: 4px;
  }

  .parent-goals-select {
    width: 100%;

    .el-input__inner {
      border-radius: 8px;
      border: 1px solid #e5e7eb;
      font-size: 13px;
      
      &:focus {
        border-color: #7c3aed;
      }
    }

    .el-select__tags {
      flex-wrap: nowrap;
    }

    .el-tag {
      background: #ede9fe;
      border-color: #c4b5fd;
      color: #7c3aed;
      font-weight: 500;
      border-radius: 6px;
    }

    .el-tag__close {
      color: #7c3aed;

      &:hover {
        background: #c4b5fd;
        color: white;
      }
    }
  }

  .parent-goal-option {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pg-team-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .pg-goal-text {
    font-size: 13px;
    color: #374151;
  }

  .no-parents-hint {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #9ca3af;
    font-style: italic;

    i {
      font-size: 13px;
      color: #d1d5db;
    }
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid #f0f0f0;
  }

  .el-form-item {
    margin-bottom: 0;
  }

  .el-form-item__label {
    padding: 0;
    margin-bottom: 8px;
    line-height: 1.5;
  }

  .cancel-btn {
    min-width: 100px;
    height: 40px;
    font-size: 15px;
    font-weight: 600;
    border: 1px solid #dcdfe6;
    background: #ffffff;
    color: #606266;
    border-radius: 4px;
    transition: all 0.2s;

    &:hover {
      border-color: #909399;
      color: #303133;
      background: #f5f7fa;
    }
  }

  .save-btn {
    min-width: 100px;
    height: 40px;
    font-size: 15px;
    font-weight: 600;
    background: linear-gradient(135deg, #52c1c7 0%, #42b8be 100%);
    border: none;
    color: #ffffff;
    border-radius: 4px;
    transition: all 0.2s;
    box-shadow: 0 2px 4px rgba(82, 193, 199, 0.2);

    &:hover {
      background: linear-gradient(135deg, #42b8be 0%, #3aa9af 100%);
      box-shadow: 0 4px 8px rgba(82, 193, 199, 0.3);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(82, 193, 199, 0.2);
    }

    &.is-loading {
      background: #52c1c7;
    }
  }
}

.column-header-text {
  color: #606266;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Weekly trend tooltip styles with sparkline */
.weekly-trend-tooltip {
  padding: 0 !important;
}

.trend-tooltip-content {
  padding: 12px 16px;
  min-width: 160px;
}

.trend-tooltip-header {
  font-size: 12px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 10px;
}

.trend-sparkline {
  width: 100%;
  height: 40px;
  margin-bottom: 10px;
}

.trend-sparkline svg {
  width: 100%;
  height: 100%;
}

.trend-tooltip-range {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 4px;
}

.trend-tooltip-range .trend-arrow {
  color: #c0c4cc;
  font-size: 11px;
}

.trend-tooltip-range .trend-current {
  color: #303133;
  font-weight: 700;
}

.trend-tooltip-period {
  text-align: center;
  font-size: 11px;
  color: #909399;
}

/* Blur dropdown options - global style needed for el-select dropdown */
.blur-option span {
  filter: blur(4px);
}

/* Parent Goals Tooltip Styles */
.parent-goals-tooltip {
  max-width: 400px !important;
}

.parent-goals-tooltip-content {
  padding: 4px 0;
}

.parent-goals-tooltip-content .tooltip-header {
  font-size: 12px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 8px;
}

.parent-goals-tooltip-content .tooltip-goal-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  font-size: 12px;
}

.parent-goals-tooltip-content .tooltip-team-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.parent-goals-tooltip-content .tooltip-team-name {
  color: #909399;
  font-weight: 500;
  white-space: nowrap;
}

.parent-goals-tooltip-content .tooltip-arrow {
  color: #c0c4cc;
}

.parent-goals-tooltip-content .tooltip-goal-name {
  color: #303133;
  font-weight: 500;
}

/* Goal Linking Modal Styles */
.goal-linking-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;

  .el-dialog__header {
    padding: 0;
    margin: 0;
  }

  .el-dialog__body {
    padding: 0;
  }

  .el-dialog__footer {
    padding: 0;
  }
}

/* Linking Dialog Header */
.linking-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, #6C30CC 0%, #9333EA 100%);
}

.linking-header-content {
  display: flex;
  align-items: center;
  gap: 14px;
}

.linking-header-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  
  i {
    font-size: 22px;
    color: white;
  }
}

.linking-header-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.linking-header-title {
  font-size: 18px;
  font-weight: 700;
  color: white;
  letter-spacing: -0.3px;
}

.linking-header-subtitle {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.linking-close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  i {
    font-size: 16px;
  }
}

/* Linking Dialog Body */
.linking-dialog-body {
  padding: 24px;
}

.current-goal-card {
  background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
  border: 1px solid #e9d5ff;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 24px;
}

.current-goal-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #7c3aed;
  margin-bottom: 8px;

  i {
    font-size: 13px;
  }
}

.current-goal-name {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.5;
}

/* Parent Goals Section */
.parent-goals-section {
  min-height: 120px;
}

/* Empty State */
.empty-parents-state {
  text-align: center;
  padding: 40px 24px;
  background: #fafafa;
  border: 2px dashed #e5e7eb;
  border-radius: 12px;
}

.empty-state-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;

  i {
    font-size: 26px;
    color: #9ca3af;
  }
}

.empty-state-title {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.empty-state-desc {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  max-width: 280px;
  margin: 0 auto;
}

/* Parent Goals Container */
.parent-goals-container {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.parent-goals-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.parent-goals-label {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.selection-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #059669;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  padding: 4px 12px;
  border-radius: 20px;

  i {
    font-size: 11px;
  }
}

.parent-goals-scroll {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: white;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
}

/* Team Goal Group */
.team-goal-group {
  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
}

.team-goal-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #f3f4f6;
  position: sticky;
  top: 0;
  z-index: 1;
}

.team-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.team-name {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  flex: 1;
}

.team-count {
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 10px;
}

/* Goal Select Items */
.team-goal-items {
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.goal-select-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid transparent;
  user-select: none;

  &:hover {
    background: #f9fafb;
  }

  &.is-checked {
    background: linear-gradient(135deg, rgba(108, 48, 204, 0.06) 0%, rgba(147, 51, 234, 0.06) 100%);
    border-color: rgba(108, 48, 204, 0.2);
  }
}

.goal-select-checkbox {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;
  margin-top: 1px;

  .is-checked & {
    background: linear-gradient(135deg, #6C30CC 0%, #9333EA 100%);
    border-color: #6C30CC;

    i {
      color: white;
      font-size: 12px;
      font-weight: bold;
    }
  }
}

.goal-select-text {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  line-height: 1.5;
  flex: 1;

  .is-checked & {
    color: #6C30CC;
    font-weight: 600;
  }
}

/* Footer */
.linking-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

.linking-cancel-btn {
  height: 42px;
  padding: 0 20px;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  transition: all 0.2s;

  &:hover {
    color: #374151;
    border-color: #d1d5db;
    background: #f9fafb;
  }
}

.linking-save-btn {
  height: 42px;
  min-width: 140px;
  padding: 0 20px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: #6b7280;
  border: none;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &.has-selection {
    background: linear-gradient(135deg, #6C30CC 0%, #9333EA 100%);
    
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 14px rgba(108, 48, 204, 0.35);
    }
  }

  &:hover:not(:disabled):not(.has-selection) {
    background: #4b5563;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  i {
    font-size: 14px;
  }
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.check-scale-enter-active,
.check-scale-leave-active {
  transition: all 0.15s ease;
}

.check-scale-enter,
.check-scale-leave-to {
  opacity: 0;
  transform: scale(0.5);
}
</style>
